import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { loadAdSettings, setAdSettingsCache } from "@/lib/adSettings";
import { confirmAction } from "@/lib/confirm";
import { groupIntoPrograms } from "@/lib/programs";
import { SPORTS } from "@/lib/sports";
import AdminContentRow from "@/components/AdminContentRow";
import CircuitsManager from "@/components/CircuitsManager";
import MetricLocationMap from "@/components/MetricLocationMap";
import MapView from "@/components/MapView";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Users, Trophy, MessageSquare, ClipboardCheck, Trash2, Check, X, ShieldCheck, ExternalLink, Flag, Mail, RotateCcw, LayoutDashboard, Database, Search, Ban, KeyRound, Dumbbell, Calendar, ArrowLeft, UserCog, UserX, ChevronDown, Smartphone, AlertTriangle, Lightbulb } from "lucide-react";

const TABS = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "accounts_all", label: "Accounts", icon: UserCog },
    { key: "content", label: "Content", icon: Database },
    { key: "circuits", label: "Circuits", icon: Trophy },
    { key: "email", label: "Email", icon: Mail },
    { key: "alerts", label: "Alerts", icon: AlertTriangle },
    { key: "flags", label: "Flagged", icon: Flag },
    { key: "history", label: "History", icon: RotateCcw },
    { key: "moderation", label: "Images", icon: ShieldCheck },
    { key: "suspended", label: "Suspended", icon: UserX },
    { key: "feature_requests", label: "Requests", icon: Lightbulb },
    { key: "ads", label: "Ads", icon: LayoutDashboard },
];

function PghImportCard() {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/import-pgh-events");
            setResult(data);
            toast.success(`PGH import done — ${data.created} created, ${data.skipped} already existed`);
        } catch {
            toast.error("Could not import PGH events");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="bg-[#141414] border border-[#2a2a2a] p-5 mb-6" data-testid="pgh-import-card">
            <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 text-[#ff4500] mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-xl tracking-wide">Import Prep Girls Hoops Events</h2>
                    <p className="text-[#a0a0a0] text-sm mt-1">
                        Adds the full Prep Girls Hoops 2027 circuit schedule as events (circuit tagged, shared
                        description, venue city/state mapped). Safe to run repeatedly — events already on the site are skipped, never duplicated.
                    </p>
                    {result && (
                        <p className="text-[#22c55e] text-xs mt-2" data-testid="pgh-import-result">
                            Done — {result.created} created, {result.skipped} already existed ({result.total} in schedule).
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    data-testid="pgh-import-btn"
                    onClick={run}
                    disabled={busy}
                    className="h-10 px-5 bg-[#ff4500] hover:bg-[#ff5722] disabled:opacity-50 text-white uppercase tracking-[0.18em] text-[11px] font-semibold whitespace-nowrap"
                >
                    {busy ? "Importing…" : "Import Events"}
                </button>
            </div>
        </div>
    );
}

function SlugBackfillCard() {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/backfill-slugs");
            setResult(data);
            toast.success(`SEO URLs generated — ${data.teams} teams, ${data.events} events updated`);
        } catch {
            toast.error("Could not generate SEO URLs");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="bg-[#141414] border border-[#2a2a2a] p-5 mb-6" data-testid="slug-backfill-card">
            <div className="flex items-start gap-3">
                <Database className="h-4 w-4 text-[#ff4500] mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                    <h2 className="font-display text-xl tracking-wide">SEO-Friendly URLs</h2>
                    <p className="text-[#a0a0a0] text-sm mt-1">
                        Give every existing team and event a readable, keyword-rich web address
                        (e.g. <span className="text-[#cfcfcf]">/teams/angelselite</span>). New listings get
                        one automatically. Old links keep working and redirect to the new address. Safe to run anytime.
                    </p>
                    {result && (
                        <p className="text-[#22c55e] text-xs mt-2" data-testid="slug-backfill-result">
                            Done — {result.teams} teams and {result.events} events updated.
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    data-testid="slug-backfill-btn"
                    onClick={run}
                    disabled={busy}
                    className="h-10 px-5 bg-[#ff4500] hover:bg-[#ff5722] disabled:opacity-50 text-white uppercase tracking-[0.18em] text-[11px] font-semibold whitespace-nowrap"
                >
                    {busy ? "Generating…" : "Generate URLs"}
                </button>
            </div>
        </div>
    );
}


function AdSettingsPanel() {
    const PLACEMENTS = [
        ["in_list", "In-list banner", "One banner at the end of the first list (teams/events)."],
        ["side_rail", "Right side margin ad", "Vertical ad in the right margin on wide desktop screens."],
        ["side_rail_left", "Left side margin ad", "Vertical ad in the left margin on wide desktop screens."],
        ["slideshow", "Slideshow ad", "Sponsored slide every 6th card in the homepage hero."],
        ["in_feed", "In-feed ad", "Banner every 6 posts in the community feed."],
        ["detail_bottom", "Listing detail banner", "Banner at the bottom of team/trainer/event/athlete detail pages, above the footer."],
    ];
    const [vals, setVals] = useState(null);
    const [stats, setStats] = useState({});
    useEffect(() => { loadAdSettings().then(setVals); }, []);
    useEffect(() => { api.get("/admin/ad-stats").then((r) => setStats(r.data || {})).catch(() => {}); }, []);
    const toggle = async (key) => {
        const next = { ...vals, [key]: !(vals[key] !== false) };
        setVals(next);
        try {
            const r = await api.put("/admin/ad-settings", { [key]: next[key] });
            setAdSettingsCache(r.data); setVals(r.data);
            toast.success("Ad settings updated");
        } catch { toast.error("Could not update ad settings"); loadAdSettings().then(setVals); }
    };
    if (!vals) return <div className="text-sm text-[#a0a0a0] py-8">Loading…</div>;
    return (
        <div className="space-y-4" data-testid="admin-ads-panel">
            <div>
                <h2 className="font-display text-2xl tracking-wide">Ad Placements</h2>
                <p className="text-sm text-[#a0a0a0] mt-1">Turn each AdSense placement on or off. Changes apply site-wide immediately.</p>
            </div>
            <div className="space-y-2">
                {PLACEMENTS.map(([key, label, desc]) => {
                    const on = vals[key] !== false;
                    const s = stats[key] || { total: 0, d30: 0, d7: 0 };
                    return (
                        <div key={key} className="flex items-center justify-between gap-4 border border-[#2a2a2a] bg-[#141414] px-4 py-3">
                            <div className="min-w-0">
                                <div className="text-white text-sm font-semibold">{label}</div>
                                <div className="text-xs text-[#a0a0a0]">{desc}</div>
                            </div>
                            <div className="flex items-center gap-5 shrink-0">
                                <div className="text-right hidden sm:block" data-testid={`ad-views-${key}`} title="Times this ad slot was shown to visitors (our own count — not Google's official impressions; clicks/revenue are not available client-side)">
                                    <div className="text-lg font-bold text-white leading-none">{(s.total || 0).toLocaleString()}<span className="text-[10px] font-normal text-[#8a8a8a] ml-1 uppercase tracking-[0.12em]">shown</span></div>
                                    <div className="text-[10px] text-[#6a6a6a] mt-1">{(s.d7 || 0).toLocaleString()} · 7d &nbsp;/&nbsp; {(s.d30 || 0).toLocaleString()} · 30d</div>
                                </div>
                                <button type="button" role="switch" aria-checked={on} data-testid={`ad-toggle-${key}`} onClick={() => toggle(key)}
                                    className={`shrink-0 relative h-6 w-11 rounded-full transition-colors ${on ? "bg-[#ff4500]" : "bg-[#3a3a3a]"}`}>
                                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
            <p className="text-xs text-[#6a6a6a]">Real ads only serve once your AdSense publisher ID and slot IDs are set and the site is live on your domain. Until then these show as labeled placeholders.</p>
            <p className="text-xs text-[#6a6a6a]"><span className="text-[#a0a0a0] font-semibold">About "shown":</span> this is our own count of how many times each slot was rendered to visitors — a views proxy, not Google's official impressions. AdSense clicks and revenue can't be read from the site (Google serves ads in a locked iframe); for those, use AdSense reporting in your Google account.</p>
        </div>
    );
}

export default function Admin() {
    const { user, loading: authLoading, logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const rawTab = searchParams.get("tab");
    const tab = TABS.some((t) => t.key === rawTab) ? rawTab : "overview";
    // Keep the active tab in the URL (?tab=) so the browser/app Back button returns
    // here to the SAME tab (and ScrollManager restores the exact scroll spot).
    const setTab = useCallback((key) => {
        setSearchParams(key && key !== "overview" ? { tab: key } : {}, { replace: true });
    }, [setSearchParams]);

    // Nav badge counts — open items needing review (clear only when resolved, not viewed).
    const [navCounts, setNavCounts] = useState({});
    useEffect(() => {
        if (!user?.is_staff) return;
        api.get("/admin/nav-counts").then((r) => setNavCounts(r.data || {})).catch(() => {});
    }, [user, tab]);

    useEffect(() => {
        if (authLoading) return;
        if (!user) { navigate("/login?next=/admin"); return; }
    }, [user, authLoading, navigate]);

    if (authLoading) return <div className="text-center text-[#a0a0a0] py-20">Loading…</div>;
    if (!user) return null;
    if (!user.is_staff) {
        return (
            <div className="max-w-lg mx-auto px-6 py-20 text-center" data-testid="admin-gate">
                <div className="flex items-center justify-center gap-2 text-[#ff4500] text-xs uppercase tracking-[0.3em] mb-3">
                    <ShieldCheck className="h-4 w-4" /> Admin only
                </div>
                <h1 className="font-display text-3xl tracking-wide mb-3">Admin account required</h1>
                <p className="text-[#a0a0a0] text-sm mb-6">
                    You're signed in as <span className="text-white font-semibold">{user.username}</span>
                    {user.email ? <> ({user.email})</> : null}, which isn't an admin account. Sign in with your
                    admin account to review reports.
                </p>
                <button
                    type="button"
                    data-testid="admin-gate-switch"
                    onClick={async () => { await logout(); navigate("/login?next=/admin"); }}
                    className="h-11 px-6 bg-[#ff4500] hover:bg-[#ff5722] text-white uppercase tracking-[0.2em] text-xs font-semibold"
                >
                    Sign in as admin
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10">
            <div className="flex items-center gap-3 text-[#ff4500] text-xs uppercase tracking-[0.3em] mb-2">
                <ShieldCheck className="h-3.5 w-3.5" /> Admin console
            </div>
            <h1 className="font-display text-3xl sm:text-5xl tracking-wide">Portal Administration</h1>
            <div className="h-px bg-[#2a2a2a] my-6" />

            <div className="flex flex-wrap gap-1 border-b border-[#2a2a2a]" data-testid="admin-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    const badge = navCounts[t.key] || 0;
                    return (
                        <button
                            key={t.key}
                            data-testid={`admin-tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            className={`relative inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-5 py-2.5 sm:py-3 text-[11px] sm:text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] border-b-2 transition-colors ${
                                active
                                    ? "border-[#ff4500] text-white"
                                    : "border-transparent text-[#a0a0a0] hover:text-white"
                            }`}
                        >
                            <Icon className="h-3.5 w-3.5" />
                            {t.label}
                            {badge > 0 && (
                                <span data-testid={`admin-tab-badge-${t.key}`} className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#ff4500] text-white text-[10px] font-bold leading-none">
                                    {badge > 99 ? "99+" : badge}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="mt-6">
                {tab === "overview" && <><SlugBackfillCard /><PghImportCard /><OverviewDashboard onGo={setTab} /></>}
                {tab === "accounts_all" && <AccountsBrowser />}
                {tab === "content" && <ContentManager />}
                {tab === "circuits" && <CircuitsManager />}
                {tab === "email" && (<><BroadcastComposer /><ContactInbox /></>)}
                {tab === "alerts" && <AlertsQueue />}
                {tab === "flags" && <FlagsQueue />}
                {tab === "history" && <ActivityHistory />}
                {tab === "moderation" && <ImageModeration />}
                {tab === "suspended" && <SuspendedUsers />}
                {tab === "feature_requests" && <FeatureRequestsQueue />}
                {tab === "ads" && <AdSettingsPanel />}
            </div>
        </div>
    );
}

function AlertsQueue() {
    const [data, setData] = useState(null);
    const [status, setStatus] = useState("open");
    const [busy, setBusy] = useState("");
    const load = useCallback(() => {
        api.get(`/admin/alerts?status=${status}`).then((r) => setData(r.data)).catch(() => setData({ alerts: [], types: [] }));
    }, [status]);
    useEffect(() => { load(); }, [load]);
    const act = async (id, action) => {
        setBusy(id);
        try { await api.post(`/admin/alerts/${id}/${action}`); load(); } finally { setBusy(""); }
    };
    const actCR = async (id, action) => {
        setBusy(id);
        try { await api.post(`/admin/change-requests/${id}/${action}`); load(); } finally { setBusy(""); }
    };
    if (!data) return <Loading />;
    const typeMeta = Object.fromEntries((data.types || []).map((t) => [t.type, t]));
    return (
        <div data-testid="admin-alerts" className="space-y-6">
            <div className="border border-[#2a2a2a] bg-[#141414] p-4">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#a0a0a0] mb-3">What triggers an alert</div>
                <div className="grid md:grid-cols-2 gap-3">
                    {(data.types || []).map((t) => (
                        <div key={t.type} className="border border-[#1f1f1f] bg-[#0f0f0f] p-3" data-testid={`alert-type-${t.type}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`h-2 w-2 rounded-full ${t.severity === "critical" ? "bg-[#ef4444]" : "bg-[#f59e0b]"}`} />
                                <span className="text-sm font-semibold text-white">{t.label}</span>
                            </div>
                            <p className="text-xs text-[#9a9a9a] leading-relaxed">{t.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex gap-1">
                {[["open", "Open"], ["reviewed", "Reviewed"], ["dismissed", "Dismissed"], ["all", "All"]].map(([k, l]) => (
                    <button key={k} data-testid={`alerts-filter-${k}`} onClick={() => setStatus(k)}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border ${status === k ? "border-[#ff4500] text-white bg-[#ff4500]/10" : "border-[#2a2a2a] text-[#a0a0a0] hover:text-white"}`}>{l}</button>
                ))}
            </div>

            {(data.alerts || []).length === 0 ? (
                <div className="text-center text-[#8a8a8a] py-16 border border-dashed border-[#2a2a2a]" data-testid="alerts-empty">No {status !== "all" ? status : ""} alerts.</div>
            ) : (
                <div className="space-y-3">
                    {data.alerts.map((a) => {
                        const meta = typeMeta[a.type] || {};
                        const critical = (a.severity || meta.severity) === "critical";
                        return (
                            <div key={a.id} data-testid={`alert-row-${a.id}`} className={`border p-4 ${critical ? "border-[#ef4444]/40 bg-[#ef4444]/5" : "border-[#f59e0b]/30 bg-[#f59e0b]/5"}`}>
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <AlertTriangle className={`h-4 w-4 ${critical ? "text-[#ef4444]" : "text-[#f59e0b]"}`} />
                                            <span className="text-sm font-semibold text-white">{a.title}</span>
                                            <span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 border border-[#333] text-[#bbb]">{meta.label || a.type}</span>
                                            {a.count > 1 && <span className="text-[10px] text-[#f59e0b]">×{a.count}</span>}
                                            {a.status !== "open" && <span className="text-[10px] uppercase text-[#22c55e]">{a.status}</span>}
                                        </div>
                                        <p className="text-sm text-[#d4d4d4] mt-1.5">{a.detail}</p>
                                        <div className="text-[11px] text-[#7a7a7a] mt-1.5 flex flex-wrap gap-x-4">
                                            <span>User: <span className="text-[#bbb]">{a.username}</span></span>
                                            {a.meta?.clear_at && <span>Lock clears: <span className="text-[#bbb]">{new Date(a.meta.clear_at).toLocaleString()}</span></span>}
                                            {a.meta?.max_miles && <span>Spread: <span className="text-[#bbb]">{a.meta.max_miles} mi</span></span>}
                                            <span>{new Date(a.updated_at || a.created_at).toLocaleString()}</span>
                                        </div>
                                        {Array.isArray(a.meta?.listings) && a.meta.listings.length > 0 && (
                                            <div className="text-[11px] text-[#8a8a8a] mt-1.5">Listings: {a.meta.listings.map((l) => `${l.label}${l.zip ? ` (${l.zip})` : ""}`).join(" · ")}</div>
                                        )}
                                        {a.type === "locked_change_request" && (
                                            <div className="text-[11px] text-[#9a9a9a] mt-2 space-y-1 border-l-2 border-[#a855f7]/40 pl-2" data-testid={`cr-detail-${a.id}`}>
                                                {(a.meta?.changes || []).map((c, i) => (
                                                    <div key={i}>Proposed <span className="text-[#bbb]">{c.field}</span>: “{String(c.from)}” → <span className="text-white">“{String(c.to)}”</span></div>
                                                ))}
                                                {a.meta?.reference && <div>Source: <a href={a.meta.reference} target="_blank" rel="noreferrer" className="text-[#38bdf8] underline break-all">{a.meta.reference}</a></div>}
                                                {a.meta?.entity_id && <a href={`/${a.meta.entity_type === "team" ? "teams" : "trainer-listings"}/${a.meta.entity_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#ff4500] underline">View listing <ExternalLink className="h-3 w-3" /></a>}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1.5 shrink-0">
                                        {a.status === "open" ? (
                                            a.type === "locked_change_request" ? (
                                                <>
                                                    <button data-testid={`cr-approve-${a.id}`} disabled={busy === a.id} onClick={() => actCR(a.id, "approve")} className="px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] bg-[#22c55e]/15 border border-[#22c55e]/40 text-[#22c55e] hover:bg-[#22c55e]/25">Approve & apply</button>
                                                    <button data-testid={`cr-reject-${a.id}`} disabled={busy === a.id} onClick={() => actCR(a.id, "reject")} className="px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] border border-[#333] text-[#a0a0a0] hover:text-white">Reject</button>
                                                </>
                                            ) : (
                                                <>
                                                    <button data-testid={`alert-review-${a.id}`} disabled={busy === a.id} onClick={() => act(a.id, "review")} className="px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] bg-[#22c55e]/15 border border-[#22c55e]/40 text-[#22c55e] hover:bg-[#22c55e]/25">Mark reviewed</button>
                                                    <button data-testid={`alert-dismiss-${a.id}`} disabled={busy === a.id} onClick={() => act(a.id, "dismiss")} className="px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] border border-[#333] text-[#a0a0a0] hover:text-white">Dismiss</button>
                                                </>
                                            )
                                        ) : (
                                            <button data-testid={`alert-reopen-${a.id}`} disabled={busy === a.id} onClick={() => act(a.id, "reopen")} className="px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] border border-[#333] text-[#a0a0a0] hover:text-white">Reopen</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const ACT_TYPES = [["team", "Teams"], ["trainer_listing", "Trainer Listings"], ["event", "Events"]];
const ACT_ACTIONS = [["created", "Created"], ["claimed", "Claimed"], ["unclaimed", "Unclaimed"], ["edited", "Edited"]];
const ACTION_STYLE = { created: "text-[#60a5fa]", claimed: "text-[#22c55e]", unclaimed: "text-red-400", edited: "text-amber-400" };

function ActivityHistory() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [types, setTypes] = useState([]);
    const [actions, setActions] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/activity", { params: { entity_types: types.join(","), actions: actions.join(",") } });
            setRows(data || []);
        } catch { setRows([]); } finally { setLoading(false); }
    }, [types, actions]);
    useEffect(() => { load(); }, [load]);

    const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
    const chip = (on) => `px-2.5 py-1 text-[11px] uppercase tracking-[0.12em] border transition-colors ${on ? "bg-[#ff4500] border-[#ff4500] text-white" : "bg-[#141414] border-[#2a2a2a] text-[#a0a0a0] hover:border-[#ff4500]"}`;
    const adminLink = (r) => `/admin?tab=content&ctype=${r.entity_type === "team" ? "teams" : r.entity_type === "trainer_listing" ? "trainers" : "events"}`;

    return (
        <div data-testid="activity-history">
            <div className="flex flex-wrap gap-4 mb-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a8a8a] mb-1.5">Listing type</div>
                    <div className="flex flex-wrap gap-1.5">
                        {ACT_TYPES.map(([v, l]) => <button key={v} data-testid={`history-type-${v}`} onClick={() => toggle(types, setTypes, v)} className={chip(types.includes(v))}>{l}</button>)}
                    </div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a8a8a] mb-1.5">Action</div>
                    <div className="flex flex-wrap gap-1.5">
                        {ACT_ACTIONS.map(([v, l]) => <button key={v} data-testid={`history-action-${v}`} onClick={() => toggle(actions, setActions, v)} className={chip(actions.includes(v))}>{l}</button>)}
                    </div>
                </div>
            </div>
            {loading ? <div className="text-sm text-[#8a8a8a]">Loading…</div> : rows.length === 0 ? (
                <div className="text-sm text-[#8a8a8a]">No activity matches these filters.</div>
            ) : (
                <div className="space-y-1.5">
                    {rows.map((r) => (
                        <div key={r.id} className="flex items-center gap-3 flex-wrap border border-[#2a2a2a] bg-[#141414] px-3 py-2 text-sm" data-testid="history-row">
                            <span className={`text-[10px] uppercase tracking-[0.15em] font-semibold w-20 ${ACTION_STYLE[r.action] || "text-[#a0a0a0]"}`}>{r.action}</span>
                            <span className="text-[10px] uppercase tracking-[0.15em] text-[#8a8a8a] w-28">{(ACT_TYPES.find(([v]) => v === r.entity_type) || [null, r.entity_type])[1]}</span>
                            <span className="text-white flex-1 min-w-[8rem] truncate">{r.entity_name || r.entity_id}</span>
                            {r.actor_username && <Link to={`/u/${r.actor_username}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#a0a0a0] hover:text-[#ff4500]">{r.actor_username}</Link>}
                            <span className="text-xs text-[#6a6a6a]">{new Date(r.at).toLocaleString()}</span>
                            {r.link && <Link to={r.link} className="text-[11px] uppercase tracking-[0.12em] text-[#ff4500] hover:underline" data-testid="history-open-listing">Listing ↗</Link>}
                            <Link to={adminLink(r)} className="text-[11px] uppercase tracking-[0.12em] text-[#60a5fa] hover:underline" data-testid="history-open-admin">Admin board ↗</Link>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


function PendingClaims() {
    const [items, setItems] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.get("/admin/pending-claims").then((r) => r.data).catch(() => []),
            api.get("/admin/pending-event-claims").then((r) => r.data).catch(() => []),
        ]).then(([teams, evs]) => { setItems(teams); setEvents(evs); })
          .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const approve = async (id) => {
        try { await api.post(`/teams/${id}/verify`); toast.success("Claim approved"); load(); }
        catch { toast.error("Failed to approve claim"); }
    };
    const reject = async (id) => {
        try { await api.post(`/admin/reject-claim/${id}`); toast.success("Claim rejected"); load(); }
        catch { toast.error("Failed to reject claim"); }
    };
    const approveEvent = async (id) => {
        try { await api.post(`/admin/verify-event-claim/${id}`); toast.success("Event claim approved"); load(); }
        catch { toast.error("Failed to approve"); }
    };
    const rejectEvent = async (id) => {
        try { await api.post(`/admin/reject-event-claim/${id}`); toast.success("Event claim rejected"); load(); }
        catch { toast.error("Failed to reject"); }
    };

    if (loading) return <Loading />;
    if (items.length === 0 && events.length === 0) return <Empty label="No claims awaiting verification" testid="admin-claims-empty" />;

    return (
        <div className="space-y-3" data-testid="admin-pending-claims">
            {items.map((t) => (
                <div key={t.id} data-testid={`pending-claim-${t.id}`} className="border border-[#2a2a2a] bg-[#141414] p-5">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="min-w-0">
                            <Link to={`/teams/${t.id}`} className="font-display text-2xl tracking-wide hover:text-[#ff4500] inline-flex items-center gap-1.5">
                                {t.name} <ExternalLink className="h-4 w-4 opacity-60" />
                            </Link>
                            <div className="text-xs text-[#a0a0a0] mt-1">Team · {t.city}, {t.state} · Claim by user: {t.owner_user_id}</div>
                        </div>
                        <div className="flex gap-2">
                            <Button data-testid={`approve-claim-${t.id}`} onClick={() => approve(t.id)} className="rounded-none bg-[#ff4500] hover:bg-[#ff5722] uppercase tracking-wider text-xs">
                                <Check className="h-4 w-4 mr-1.5" /> Approve
                            </Button>
                            <Button data-testid={`reject-claim-${t.id}`} onClick={() => reject(t.id)} className="rounded-none bg-transparent border border-[#2a2a2a] hover:border-[#ff4500] uppercase tracking-wider text-xs">
                                <X className="h-4 w-4 mr-1.5" /> Reject
                            </Button>
                        </div>
                    </div>
                    {t.claim_message && (
                        <div className="mt-3 border-l-2 border-[#ff4500] pl-3 text-sm text-[#cfcfcf] italic">"{t.claim_message}"</div>
                    )}
                    {(t.claim_proof_link || t.claim_proof_image) && (
                        <div className="mt-2 flex items-center gap-3" data-testid={`claim-proof-${t.id}`}>
                            {t.claim_proof_link && <a href={t.claim_proof_link} target="_blank" rel="noopener noreferrer" className="text-[11px] uppercase tracking-[0.2em] text-[#ff4500] hover:underline inline-flex items-center gap-1">Proof link <ExternalLink className="h-3 w-3" /></a>}
                            {t.claim_proof_image && <a href={t.claim_proof_image} target="_blank" rel="noopener noreferrer"><img src={t.claim_proof_image} alt="proof" className="h-14 w-14 object-cover border border-[#2a2a2a]" /></a>}
                        </div>
                    )}
                </div>
            ))}
            {events.map((t) => (
                <div key={t.id} data-testid={`pending-event-claim-${t.id}`} className="border border-[#2a2a2a] bg-[#141414] p-5">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="min-w-0">
                            <Link to={`/events/${t.id}`} className="font-display text-2xl tracking-wide hover:text-[#22c55e] inline-flex items-center gap-1.5">
                                {t.name} <ExternalLink className="h-4 w-4 opacity-60" />
                            </Link>
                            <div className="text-xs text-[#a0a0a0] mt-1">Event · {t.city}, {t.state} · Claim by: {t.claimant_username ? `${t.claimant_username}` : t.owner_user_id}{t.claimant_email ? ` (${t.claimant_email})` : ""}</div>
                        </div>
                        <div className="flex gap-2">
                            <Button data-testid={`approve-event-claim-${t.id}`} onClick={() => approveEvent(t.id)} className="rounded-none bg-[#22c55e] hover:bg-[#16a34a] uppercase tracking-wider text-xs">
                                <Check className="h-4 w-4 mr-1.5" /> Approve
                            </Button>
                            <Button data-testid={`reject-event-claim-${t.id}`} onClick={() => rejectEvent(t.id)} className="rounded-none bg-transparent border border-[#2a2a2a] hover:border-[#22c55e] uppercase tracking-wider text-xs">
                                <X className="h-4 w-4 mr-1.5" /> Reject
                            </Button>
                        </div>
                    </div>
                    {t.claim_message && (
                        <div className="mt-3 border-l-2 border-[#22c55e] pl-3 text-sm text-[#cfcfcf] italic">"{t.claim_message}"</div>
                    )}
                    {(t.claim_proof_link || t.claim_proof_image) && (
                        <div className="mt-2 flex items-center gap-3" data-testid={`event-claim-proof-${t.id}`}>
                            {t.claim_proof_link && <a href={t.claim_proof_link} target="_blank" rel="noopener noreferrer" className="text-[11px] uppercase tracking-[0.2em] text-[#22c55e] hover:underline inline-flex items-center gap-1">Proof link <ExternalLink className="h-3 w-3" /></a>}
                            {t.claim_proof_image && <a href={t.claim_proof_image} target="_blank" rel="noopener noreferrer"><img src={t.claim_proof_image} alt="proof" className="h-14 w-14 object-cover border border-[#2a2a2a]" /></a>}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function TeamsManager({ sports = [], query = "", source = "all" }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        api.get("/admin/teams")
            .then((r) => setItems(r.data))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const remove = async (id, name) => {
        if (!window.confirm(`Delete "${name}"? This removes reviews and favorites too. This cannot be undone.`)) return;
        try { await api.delete(`/teams/${id}`); toast.success("Team deleted"); load(); }
        catch { toast.error("Failed to delete"); }
    };

    const visible = items.filter((t) => {
        if (sports.length && !sports.includes(t.sport)) return false;
        if (source === "seed" && !t.is_seed) return false;
        if (source === "user" && t.is_seed) return false;
        if (!query) return true;
        const q = query.toLowerCase();
        return (t.name || "").toLowerCase().includes(q)
            || (t.city || "").toLowerCase().includes(q)
            || (t.state || "").toLowerCase().includes(q);
    });
    // Collapse rosters into one row per PROGRAM. The representative carries the admin
    // fields (owner/created_by/claimed/hidden); we show the program base name only.
    const programRows = groupIntoPrograms(visible).map((p) => ({
        ...p.representative,
        name: p.base_name,
        age_group: "",
    }));

    if (loading) return <Loading />;

    return (
        <div data-testid="admin-teams-manager">
            <div className="flex items-center justify-end mb-3 gap-3">
                <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">{programRows.length} programs</div>
            </div>
            <div className="space-y-2">
                {programRows.length === 0 ? <Empty label="Nothing found" testid="admin-teams-empty" /> : programRows.map((t) => (
                    <AdminContentRow key={t.id} kind="teams" item={t} onChange={load} />
                ))}
            </div>
        </div>
    );
}

function ReviewsModeration({ sports = [], query = "" }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        api.get("/admin/reviews")
            .then((r) => setItems(r.data))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const remove = async (id) => {
        if (!window.confirm("Delete this review? This cannot be undone.")) return;
        try { await api.delete(`/reviews/${id}`); toast.success("Review deleted"); load(); }
        catch { toast.error("Failed to delete review"); }
    };

    const q = query.trim().toLowerCase();
    const visible = items.filter((r) => {
        if (sports.length && !sports.includes(r.sport)) return false;
        if (!q) return true;
        return (r.team_name || "").toLowerCase().includes(q)
            || (r.comment || "").toLowerCase().includes(q)
            || (r.author_name || "").toLowerCase().includes(q);
    });
    if (loading) return <Loading />;
    if (visible.length === 0) return <Empty label="No reviews yet" testid="admin-reviews-empty" />;

    return (
        <div className="space-y-3" data-testid="admin-reviews">
            {visible.map((r) => (
                <div key={r.id} data-testid={`admin-review-${r.id}`} className="border border-[#2a2a2a] bg-[#141414] p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">
                                {r.rating}★ · {r.user_username || r.user_name || "Anon"} · <Link to={`${r.entity_type === "event" ? "/events" : r.entity_type === "trainer_listing" ? "/trainer-listings" : "/teams"}/${r.entity_id || r.team_id}`} className="hover:underline">{r.entity_name || r.team_name}</Link>
                            </div>
                            <div className="font-display text-xl tracking-wide mt-1">{r.title}</div>
                        </div>
                        <Button data-testid={`admin-delete-review-${r.id}`} onClick={() => remove(r.id)} className="rounded-none bg-transparent border border-[#2a2a2a] hover:border-red-500 hover:text-red-500 uppercase tracking-wider text-xs h-9">
                            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                        </Button>
                    </div>
                    <p className="text-sm text-[#cfcfcf] mt-2">{r.body}</p>
                </div>
            ))}
        </div>
    );
}

function FlagsQueue() {
    const [items, setItems] = useState([]);
    const [deleted, setDeleted] = useState([]);
    const [circuitReqs, setCircuitReqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [digestBusy, setDigestBusy] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.get("/flags", { params: { status: "open" } }).then((r) => r.data.items || []).catch(() => []),
            api.get("/flags/deleted").then((r) => r.data.items || []).catch(() => []),
            api.get("/circuit-requests").then((r) => r.data.items || []).catch(() => []),
        ]).then(([open, del, reqs]) => { setItems(open); setDeleted(del); setCircuitReqs(reqs); })
          .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const dismiss = async (id) => {
        try { await api.post(`/flags/${id}/dismiss`); setItems((x) => x.filter((f) => f.id !== id)); toast.success("Dismissed"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const deleteTarget = async (id) => {
        try { await api.post(`/flags/${id}/delete-target`); toast.success("Content removed — restorable for 7 days"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const restore = async (id) => {
        try { await api.post(`/flags/deleted/${id}/restore`); toast.success("Content restored"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const resolveCircuit = async (id) => {
        try { await api.post(`/circuit-requests/${id}/resolve`); toast.success("Circuit request resolved"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const runDigest = async () => {
        setDigestBusy(true);
        try {
            const { data } = await api.post("/notifications/digest/run");
            toast.success(`Digest: ${data.opted_in} opted-in, ${data.emails_sent} sent (email dormant until Resend key set)`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setDigestBusy(false); }
    };

    return (
        <div className="space-y-3" data-testid="admin-flags">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">{items.length} open flag{items.length === 1 ? "" : "s"}</div>
                <Button data-testid="admin-run-digest" onClick={runDigest} disabled={digestBusy} className="rounded-none bg-[#141414] border border-[#ff4500] text-[#ff4500] hover:bg-[#ff4500] hover:text-white uppercase tracking-[0.2em] text-[10px] h-9">
                    <Mail className="h-3.5 w-3.5 mr-1.5" /> {digestBusy ? "Running…" : "Run weekly digest"}
                </Button>
            </div>
            {loading ? <Loading /> : items.length === 0 ? (
                <Empty label="No open flags — the queue is clear." testid="admin-flags-empty" />
            ) : (
                items.map((f) => (
                    <div key={f.id} className="border border-[#2a2a2a] bg-[#141414] p-4 flex items-start justify-between gap-4" data-testid={`admin-flag-${f.id}`}>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-red-400">{f.target_type} · reported by {f.reporter_username}</div>
                            <div className="text-sm text-white mt-0.5">{f.preview?.label || "(item)"}</div>
                            {f.preview?.text && <div className="text-xs text-[#a0a0a0] mt-0.5 line-clamp-2">{f.preview.text}</div>}
                            <div className="text-xs text-[#cfcfcf] mt-1"><span className="text-[#a0a0a0]">Reason:</span> {f.reason}</div>
                            {f.preview?.link && <Link to={f.preview.link} className="text-[11px] uppercase tracking-[0.2em] text-[#ff4500] hover:underline inline-flex items-center gap-1 mt-1">View <ExternalLink className="h-3 w-3" /></Link>}
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                            <Button data-testid={`flag-dismiss-${f.id}`} onClick={() => dismiss(f.id)} className="rounded-none bg-[#141414] border border-[#2a2a2a] hover:border-white text-xs uppercase tracking-[0.15em] h-8">Dismiss</Button>
                            <Button data-testid={`flag-delete-${f.id}`} onClick={() => deleteTarget(f.id)} className="rounded-none bg-red-600 hover:bg-red-500 text-xs uppercase tracking-[0.15em] h-8"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
                        </div>
                    </div>
                ))
            )}

            {deleted.length > 0 && (
                <div className="mt-6" data-testid="admin-deleted-items">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Recently removed · restorable for 7 days</div>
                    {deleted.map((d) => (
                        <div key={d.id} className="border border-[#2a2a2a] bg-[#0f0f0f] p-3 flex items-center justify-between gap-4 mb-2" data-testid={`deleted-item-${d.id}`}>
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">{d.target_type} · removed by {d.deleted_by}</div>
                                <div className="text-sm text-white truncate">{d.label || "(item)"}</div>
                            </div>
                            <Button data-testid={`restore-${d.id}`} onClick={() => restore(d.id)} className="rounded-none bg-[#141414] border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e] hover:text-white text-xs uppercase tracking-[0.15em] h-8 shrink-0">
                                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                            </Button>
                        </div>
                    ))}
                </div>
            )}
            {circuitReqs.length > 0 && (
                <div className="mt-6" data-testid="admin-circuit-requests">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Circuit requests · proposed for the search filter</div>
                    {circuitReqs.map((c) => (
                        <div key={c.id} className="border border-[#2a2a2a] bg-[#0f0f0f] p-3 flex items-center justify-between gap-4 mb-2" data-testid={`circuit-request-${c.id}`}>
                            <div className="min-w-0">
                                <div className="text-sm text-white truncate">{c.circuit} <span className="text-[#a0a0a0]">· {c.sport}</span></div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">requested {c.count || 1}× · by {c.requested_by_username || "user"}</div>
                            </div>
                            <Button data-testid={`resolve-circuit-${c.id}`} onClick={() => resolveCircuit(c.id)} className="rounded-none bg-[#141414] border border-[#2a2a2a] hover:border-white text-xs uppercase tracking-[0.15em] h-8 shrink-0">Resolve</Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Loading() {
    return <div className="text-center text-[#a0a0a0] py-16">Loading…</div>;
}

function Empty({ label, testid }) {
    return (
        <div className="border border-[#2a2a2a] py-16 text-center" data-testid={testid}>
            <div className="font-display text-2xl">{label}</div>
        </div>
    );
}

// ============================ Contact form inbox ============================
function ContactInbox() {
    const [items, setItems] = useState(null);
    useEffect(() => { api.get("/admin/contact-messages").then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, []);
    return (
        <div className="max-w-2xl mt-10 pt-8 border-t border-[#2a2a2a]" data-testid="admin-contact-inbox">
            <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-3 flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> Contact form inbox {items ? `(${items.length})` : ""}</div>
            {items == null ? <Loading /> : items.length === 0 ? (
                <div className="text-sm text-[#666] border border-[#2a2a2a] p-6 text-center">No messages yet.</div>
            ) : (
                <div className="space-y-2">
                    {items.map((m) => (
                        <div key={m.id} className="border border-[#2a2a2a] bg-[#141414] p-4" data-testid={`contact-msg-${m.id}`}>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="text-sm text-white font-semibold">{m.subject || "(no subject)"}</div>
                                <div className="text-[11px] text-[#666]">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</div>
                            </div>
                            <div className="text-[11px] text-[#a0a0a0] mt-0.5">{m.name || "Anonymous"} · <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || "your message")}`} className="text-[#4aa3f0] hover:underline">{m.email}</a></div>
                            <div className="text-sm text-[#cfcfcf] mt-2 whitespace-pre-line">{m.message}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ============================ Admin broadcast email ============================
function BroadcastComposer() {
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const send = async (testOnly) => {
        if (!subject.trim() || !message.trim()) { toast.error("Add a subject and message"); return; }
        if (!testOnly && !window.confirm("Send this email to ALL members with an email address?")) return;
        setBusy(true);
        setResult(null);
        try {
            const { data } = await api.post("/admin/broadcast", { subject, message, test_only: testOnly });
            setResult(data);
            if (testOnly) toast.success(data.delivered ? "Test email sent to you" : "Test queued (domain not verified yet)");
            else toast.success(`Broadcast: ${data.sent}/${data.recipients} delivered`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to send");
        } finally { setBusy(false); }
    };

    return (
        <div className="max-w-2xl" data-testid="admin-broadcast">
            <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-3 flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> Email your members</div>
            <div className="space-y-4">
                <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Subject</label>
                    <input data-testid="broadcast-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={160} className="mt-2 w-full h-11 bg-[#141414] border border-[#2a2a2a] px-3 text-sm text-white outline-none focus:border-[#ff4500]" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Message</label>
                    <textarea data-testid="broadcast-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={9} maxLength={20000} placeholder="Write your announcement… line breaks are preserved." className="mt-2 w-full bg-[#141414] border border-[#2a2a2a] px-3 py-2 text-sm text-white outline-none focus:border-[#ff4500] resize-y" />
                </div>
                <div className="flex flex-wrap gap-3">
                    <Button data-testid="broadcast-test" disabled={busy} onClick={() => send(true)} className="rounded-none bg-[#141414] border border-[#2a2a2a] hover:border-white text-xs uppercase tracking-[0.15em] h-10">Send test to me</Button>
                    <Button data-testid="broadcast-send" disabled={busy} onClick={() => send(false)} className="rounded-none bg-[#ff4500] hover:bg-[#e03e00] text-xs uppercase tracking-[0.15em] h-10"><Mail className="h-4 w-4 mr-1.5" /> Send to all members</Button>
                </div>
                {result && (
                    <div className="border border-[#2a2a2a] bg-[#141414] p-3 text-sm text-[#cfcfcf]" data-testid="broadcast-result">
                        {result.test_only
                            ? <>Test send: {result.delivered ? "delivered ✓" : `not delivered — ${result.error || "domain not verified"}`}</>
                            : <>Sent to {result.sent}/{result.recipients} members{result.failed ? `, ${result.failed} failed` : ""}. {result.error ? <span className="text-[#f59e0b]">First error: {result.error}</span> : null}</>}
                    </div>
                )}
                <p className="text-[11px] text-[#666]">Until <span className="text-[#a0a0a0]">travelsports.info</span> is verified in Resend, delivery is limited to the Resend account owner's address. Use "Send test to me" to preview.</p>
            </div>
        </div>
    );
}

// ============================ Unverified circuits tracker ============================
function CircuitsTracker() {
    const [items, setItems] = useState(null);

    const load = useCallback(() => {
        api.get("/circuits/admin").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    }, []);
    useEffect(() => { load(); }, [load]);

    const verify = async (id, name) => {
        try { await api.post(`/circuits/${id}/verify`); toast.success(`"${name}" moved to the main circuit list`); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const unverify = async (id) => {
        try { await api.post(`/circuits/${id}/unverify`); toast.success("Circuit moved back to unverified"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const remove = async (id, name, usage) => {
        const u = (usage?.teams || 0) + (usage?.events || 0);
        const warn = u ? ` ${u} listing(s) using it will be reset to "Other".` : "";
        if (!window.confirm(`Delete circuit "${name}"?${warn} This cannot be undone.`)) return;
        try { await api.delete(`/circuits/${id}`); toast.success("Circuit deleted"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    if (!items) return <Loading />;
    const unverified = items.filter((c) => !c.verified);
    const verified = items.filter((c) => c.verified);

    const Row = ({ c }) => (
        <div className="border border-[#2a2a2a] bg-[#141414] p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-circuit-${c.id}`}>
            <div className="min-w-0">
                <div className="text-sm text-white font-semibold inline-flex items-center gap-1.5">
                    {!c.verified && <Flag className="h-3.5 w-3.5 text-[#f59e0b]" />}{c.name}
                    <span className="text-[10px] uppercase tracking-wider text-[#a0a0a0]">· {c.sport}</span>
                </div>
                <div className="text-[11px] text-[#a0a0a0] mt-0.5">
                    added by {c.created_by_username ? `${c.created_by_username}` : "user"}
                    {c.created_at ? ` · ${new Date(c.created_at).toLocaleDateString()}` : ""}
                    {" · "}{c.usage.teams} team{c.usage.teams === 1 ? "" : "s"}, {c.usage.events} event{c.usage.events === 1 ? "" : "s"}
                </div>
            </div>
            <div className="flex gap-2 shrink-0">
                {c.verified ? (
                    <Button data-testid={`circuit-unverify-${c.id}`} onClick={() => unverify(c.id)} className="rounded-none bg-[#141414] border border-[#2a2a2a] hover:border-white text-xs uppercase tracking-[0.15em] h-8">Unverify</Button>
                ) : (
                    <Button data-testid={`circuit-verify-${c.id}`} onClick={() => verify(c.id, c.name)} className="rounded-none bg-[#22c55e] hover:bg-[#16a34a] text-xs uppercase tracking-[0.15em] h-8"><Check className="h-3.5 w-3.5 mr-1" /> Verify</Button>
                )}
                <Button data-testid={`circuit-delete-${c.id}`} onClick={() => remove(c.id, c.name, c.usage)} className="rounded-none bg-transparent border border-[#2a2a2a] hover:border-red-500 hover:text-red-500 text-xs uppercase tracking-[0.15em] h-8"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
        </div>
    );

    return (
        <div data-testid="admin-circuits-tracker">
            <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-2 flex items-center gap-2">
                <Flag className="h-3.5 w-3.5 text-[#f59e0b]" /> {unverified.length} unverified circuit{unverified.length === 1 ? "" : "s"} pending review
            </div>
            {unverified.length === 0 ? (
                <Empty label="No unverified circuits — all reviewed." testid="admin-circuits-empty" />
            ) : (
                <div className="space-y-2">{unverified.map((c) => <Row key={c.id} c={c} />)}</div>
            )}
            {verified.length > 0 && (
                <div className="mt-6">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Verified user circuits · in the main list</div>
                    <div className="space-y-2">{verified.map((c) => <Row key={c.id} c={c} />)}</div>
                </div>
            )}
        </div>
    );
}



// ============================ Overview dashboard ============================
const METRICS = [
    { key: "signups", label: "Signups", color: "#22c55e" },
    { key: "visits", label: "Site visits", color: "#ff4500" },
    { key: "follows", label: "Follows", color: "#a855f7" },
    { key: "shares", label: "Shares", color: "#f59e0b" },
    { key: "posts", label: "Posts", color: "#60a5fa" },
    { key: "reviews", label: "Reviews", color: "#ef4444" },
];
const GRANS = [["day", "Daily"], ["week", "Weekly"], ["month", "Monthly"], ["year", "Yearly"]];
const isoDate = (d) => d.toISOString().slice(0, 10);

// Clickable metric picker + date-range + granularity, driving a bar graph.
// Controlled: selection/range state lives in OverviewDashboard so the detail panel shares it.
function MetricsGraph({ totals = {}, metric, setMetric, gran, setGran, from, setFrom, to, setTo }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get("/admin/metrics/timeseries", { params: { metric, granularity: gran, from, to } })
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => { if (alive) setData({ series: [], total: 0 }); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [metric, gran, from, to]);

    const active = METRICS.find((m) => m.key === metric) || METRICS[0];
    const series = data?.series || [];
    const maxVal = Math.max(1, ...series.map((s) => s.value));
    const showLabels = series.length <= 40;

    return (
        <div className="border border-[#2a2a2a] bg-[#141414] p-3 sm:p-4 mb-5 min-w-0" data-testid="metrics-graph">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mb-4" data-testid="metric-picker">
                {METRICS.map((m) => {
                    const on = m.key === metric;
                    return (
                        <button key={m.key} data-testid={`metric-${m.key}`} onClick={() => setMetric(m.key)}
                            className={`text-center border px-1.5 py-2 min-w-0 transition-colors ${on ? "bg-[#ff4500]/10" : "bg-[#0f0f0f] hover:border-[#ff4500]/60"}`}
                            style={{ borderColor: on ? m.color : "#2a2a2a" }}>
                            <div className="text-[9px] leading-tight uppercase tracking-[0.06em] text-[#a0a0a0] truncate">{m.label}</div>
                            <div className="text-base font-bold leading-none mt-1" style={{ color: on ? m.color : "#fff" }}>{(totals[m.key] ?? 0).toLocaleString()}</div>
                            <div className="text-[8px] text-[#666] uppercase tracking-[0.08em] mt-0.5">30d</div>
                        </button>
                    );
                })}
            </div>
            <div className="flex flex-wrap items-end gap-2 sm:gap-3 mb-4">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#8a8a8a] mb-1">From</div>
                    <input type="date" data-testid="metric-from" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
                        className="h-9 w-[140px] bg-[#0f0f0f] border border-[#2a2a2a] text-white text-xs px-2 outline-none focus:border-[#ff4500] [color-scheme:dark]" />
                </div>
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#8a8a8a] mb-1">To</div>
                    <input type="date" data-testid="metric-to" value={to} min={from} max={isoDate(new Date())} onChange={(e) => setTo(e.target.value)}
                        className="h-9 w-[140px] bg-[#0f0f0f] border border-[#2a2a2a] text-white text-xs px-2 outline-none focus:border-[#ff4500] [color-scheme:dark]" />
                </div>
                <div className="flex gap-1.5 flex-wrap">
                    {GRANS.map(([k, l]) => (
                        <button key={k} data-testid={`gran-${k}`} onClick={() => setGran(k)}
                            className={`px-2.5 sm:px-3 h-9 text-[11px] uppercase tracking-[0.12em] border ${gran === k ? "border-[#ff4500] text-white bg-[#ff4500]/10" : "border-[#2a2a2a] text-[#a0a0a0] hover:text-white"}`}>{l}</button>
                    ))}
                </div>
                <div className="ml-auto text-right">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#8a8a8a]">Total · {active.label}</div>
                    <div className="text-2xl font-bold" style={{ color: active.color }} data-testid="metric-total">{(data?.total ?? 0).toLocaleString()}</div>
                </div>
            </div>
            {loading ? (
                <div className="h-40 flex items-center justify-center text-sm text-[#8a8a8a]">Loading…</div>
            ) : series.length === 0 ? (
                <div className="h-40 flex items-center justify-center text-sm text-[#8a8a8a]" data-testid="metric-empty">No data in this range.</div>
            ) : (
                <div className="overflow-x-auto">
                    <div className="flex items-end gap-1 h-40 min-w-full" style={{ minWidth: series.length * 14 }}>
                        {series.map((s, i) => (
                            <div key={i} className="flex-1 h-full flex flex-col items-center justify-end gap-1 group" style={{ minWidth: 8 }}>
                                <span className="text-[9px] text-white opacity-0 group-hover:opacity-100 transition-opacity -mb-0.5">{s.value}</span>
                                <div className="w-full rounded-t transition-all" title={`${s.label}: ${s.value}`}
                                    style={{ height: `${(s.value / maxVal) * 100}%`, minHeight: s.value > 0 ? 3 : 1, background: `linear-gradient(180deg, ${active.color}, ${active.color}99)` }} />
                                {showLabels && <span className="text-[8px] text-[#666] whitespace-nowrap rotate-0">{s.label}</span>}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// Small 5/10/25/50 "show N" dropdown.
const COUNT_OPTS = [5, 10, 25, 50];
function CountSelect({ value, onChange, testid = "count-select" }) {
    return (
        <select
            data-testid={testid}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            className="h-7 bg-[#0f0f0f] border border-[#2a2a2a] text-[#cfcfcf] text-[11px] uppercase tracking-[0.1em] px-2 outline-none focus:border-[#ff4500] [color-scheme:dark] cursor-pointer"
        >
            {COUNT_OPTS.map((n) => <option key={n} value={n}>Show {n}</option>)}
        </select>
    );
}

// Map a metric geo point to its detail page. Listing points (team/trainer/event) link to
// their detail page; user-location points (signups/visits/posts) have no detail page → null
// (the pin still shows the hover/tap preview, it just isn't clickable through).
function metricLinkResolver(t) {
    if (!t || !t.id) return null;
    if (t.type === "event") return `/events/${t.id}`;
    if (t.type === "trainer") return `/trainer-listings/${t.id}`;
    if (t.type === "team") return `/teams/${t.id}`;
    return null;
}

// Map of the selected metric's locations (metrics that carry a location).
function MetricMap({ metric, from, to }) {
    const [data, setData] = useState(null);
    const [mode, setMode] = useState("pins"); // "pins" | "heat"
    useEffect(() => {
        let alive = true;
        setData(null);
        api.get("/admin/metrics/geo", { params: { metric, from, to } })
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => { if (alive) setData({ points: [], note: "" }); });
        return () => { alive = false; };
    }, [metric, from, to]);
    const cfg = METRICS.find((m) => m.key === metric) || METRICS[0];
    const pts = data?.points || [];
    return (
        <div className="border border-[#2a2a2a] bg-[#141414] p-3 sm:p-4 min-w-0" data-testid="metric-map">
            <div className="text-[11px] uppercase tracking-[0.1em] text-[#a0a0a0] mb-2 flex items-center justify-between gap-2">
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: cfg.color }} /> {cfg.label} map</span>
                <div className="flex items-center gap-3">
                    <div className="inline-flex border border-[#2a2a2a] rounded overflow-hidden" data-testid="map-mode-toggle">
                        {[["pins", "Pins"], ["heat", "Heat"]].map(([k, l]) => (
                            <button key={k} data-testid={`map-mode-${k}`} onClick={() => setMode(k)}
                                className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] ${mode === k ? "bg-[#ff4500] text-white" : "text-[#a0a0a0] hover:text-white"}`}>{l}</button>
                        ))}
                    </div>
                    <span className="text-[#6a6a6a] normal-case tracking-normal">{pts.length} located</span>
                </div>
            </div>
            {data === null ? <div className="h-[320px] flex items-center justify-center text-sm text-[#666]">Loading…</div>
                : pts.length === 0 ? <div className="h-[320px] flex items-center justify-center text-center text-sm text-[#666] px-6" data-testid="metric-map-empty">{data.note || "No mappable locations for this metric."}</div>
                : mode === "heat"
                    ? <MetricLocationMap points={pts} color={cfg.color} mode="heat" height="320px" />
                    : <MapView teams={pts} height="320px" linkResolver={metricLinkResolver} />}
            {pts.length > 0 && data.note ? <div className="text-[11px] text-[#6a6a6a] mt-2">{data.note}</div> : null}
        </div>
    );
}

// Recent individual events behind the selected metric (replaces old Pending queue box).
function MetricDetail({ metric, from, to }) {
    const [rows, setRows] = useState(null);
    const [limit, setLimit] = useState(10);
    useEffect(() => {
        let alive = true;
        setRows(null);
        api.get("/admin/metrics/details", { params: { metric, from, to, limit } })
            .then((r) => { if (alive) setRows(r.data?.rows || []); })
            .catch(() => { if (alive) setRows([]); });
        return () => { alive = false; };
    }, [metric, from, to, limit]);
    const cfg = METRICS.find((m) => m.key === metric) || METRICS[0];
    return (
        <div className="border border-[#2a2a2a] bg-[#141414] p-3 sm:p-4 min-w-0" data-testid="metric-detail">
            <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-[11px] uppercase tracking-[0.1em] text-[#a0a0a0] flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: cfg.color }} /> Recent {cfg.label.toLowerCase()}
                </span>
                <CountSelect value={limit} onChange={setLimit} testid="metric-detail-count" />
            </div>
            <div className="max-h-[300px] overflow-y-auto -mr-1 pr-1">
                {rows === null ? <div className="text-sm text-[#666] py-2">Loading…</div>
                    : rows.length === 0 ? <div className="text-sm text-[#666] py-2" data-testid="metric-detail-empty">No {cfg.label.toLowerCase()} in this range.</div>
                    : rows.map((r, i) => {
                        const inner = (
                            <>
                                <div className="min-w-0">
                                    <div className="text-white truncate group-hover:text-[#ff4500] transition-colors">{r.title}</div>
                                    <div className="text-[11px] text-[#8a8a8a] truncate">{r.sub}</div>
                                </div>
                                <div className="text-[11px] text-[#6a6a6a] shrink-0 whitespace-nowrap">{r.at ? new Date(r.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</div>
                            </>
                        );
                        const cls = "group flex items-start justify-between gap-3 py-2 border-b border-[#1a1a1a] last:border-0 text-sm";
                        return r.link
                            ? <Link key={i} to={r.link} target="_blank" rel="noopener noreferrer" data-testid="metric-detail-row" className={cls}>{inner}</Link>
                            : <div key={i} data-testid="metric-detail-row" className={cls}>{inner}</div>;
                    })}
            </div>
        </div>
    );
}

// Newest content across selected types (replaces the old Newest members box).
const CONTENT_KIND = {
    team: { label: "Teams", color: "#ff4500" },
    trainer: { label: "Trainers", color: "#2196f3" },
    event: { label: "Events", color: "#22c55e" },
    athlete: { label: "Athletes", color: "#a855f7" },
    review: { label: "Reviews", color: "#f59e0b" },
};
const CONTENT_KIND_KEYS = Object.keys(CONTENT_KIND);
function NewestContent() {
    const [types, setTypes] = useState(CONTENT_KIND_KEYS);
    const [limit, setLimit] = useState(10);
    const [items, setItems] = useState(null);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);
    useEffect(() => {
        let alive = true;
        setItems(null);
        if (types.length === 0) { setItems([]); return; }
        api.get("/admin/newest-content", { params: { types: types.join(","), limit } })
            .then((r) => { if (alive) setItems(r.data?.items || []); })
            .catch(() => { if (alive) setItems([]); });
        return () => { alive = false; };
    }, [types, limit]);
    const toggle = (k) => setTypes((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
    const allOn = types.length === CONTENT_KIND_KEYS.length;
    const label = types.length === 0 ? "None" : allOn ? "All types" : types.length === 1 ? CONTENT_KIND[types[0]].label : `${types.length} types`;
    return (
        <div className="border border-[#2a2a2a] bg-[#141414] p-3 sm:p-4 min-w-0" data-testid="newest-content">
            <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-[11px] uppercase tracking-[0.1em] text-[#a0a0a0]">Newest content</span>
                <div className="flex items-center gap-2">
                    <div className="relative" ref={ref}>
                        <button type="button" data-testid="newest-content-type-btn" onClick={() => setOpen((o) => !o)}
                            className="inline-flex items-center gap-1.5 h-7 px-2 text-[11px] uppercase tracking-[0.1em] border border-[#2a2a2a] text-[#cfcfcf] bg-[#0f0f0f] hover:border-[#ff4500]">
                            {label} <ChevronDown className="h-3 w-3 text-[#a0a0a0]" />
                        </button>
                        {open && (
                            <div className="absolute right-0 z-50 mt-1 w-44 bg-[#0f0f0f] border border-[#2a2a2a] shadow-xl p-1" data-testid="newest-content-type-panel">
                                <button type="button" data-testid="newest-content-opt-all" onClick={() => setTypes(allOn ? [] : [...CONTENT_KIND_KEYS])}
                                    className={`w-full text-left px-3 py-2 text-xs uppercase tracking-wider flex items-center gap-2 ${allOn ? "text-[#ff4500]" : "text-[#cfcfcf] hover:bg-[#1a1a1a]"}`}>
                                    <span className="w-3.5 inline-flex">{allOn ? <Check className="h-3.5 w-3.5" /> : null}</span>All types
                                </button>
                                {CONTENT_KIND_KEYS.map((k) => {
                                    const on = types.includes(k);
                                    return (
                                        <button key={k} type="button" data-testid={`newest-content-opt-${k}`} onClick={() => toggle(k)}
                                            className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 ${on ? "text-[#ff4500]" : "text-[#cfcfcf] hover:bg-[#1a1a1a]"}`}>
                                            <span className="w-3.5 inline-flex">{on ? <Check className="h-3.5 w-3.5" /> : null}</span>{CONTENT_KIND[k].label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <CountSelect value={limit} onChange={setLimit} testid="newest-content-count" />
                </div>
            </div>
            <div className="max-h-[300px] overflow-y-auto -mr-1 pr-1">
                {items === null ? <div className="text-sm text-[#666] py-2">Loading…</div>
                    : items.length === 0 ? <div className="text-sm text-[#666] py-2" data-testid="newest-content-empty">{types.length === 0 ? "Select a content type." : "Nothing found."}</div>
                    : items.map((it, i) => {
                        const kc = CONTENT_KIND[it.type] || {};
                        return (
                            <div key={i} data-testid="newest-content-row" className="flex items-start justify-between gap-3 py-2 border-b border-[#1a1a1a] last:border-0 text-sm">
                                <span className="flex flex-col min-w-0 flex-1 gap-0.5">
                                    <span className="flex items-center gap-2 min-w-0">
                                        <span className="text-[9px] uppercase tracking-[0.12em] font-semibold w-14 shrink-0" style={{ color: kc.color }}>{kc.label ? kc.label.replace(/s$/, "") : it.type}</span>
                                        {it.link
                                            ? <Link to={it.link} target="_blank" rel="noopener noreferrer" className="text-white truncate min-w-0 hover:text-[#ff4500]">{it.name}</Link>
                                            : <span className="text-white truncate min-w-0">{it.name}</span>}
                                    </span>
                                    {it.by && <span className="pl-16 min-w-0"><Link to={`/u/${it.by}`} target="_blank" rel="noopener noreferrer" data-testid="newest-content-by" className="text-[11px] text-[#6a6a6a] hover:text-[#ff4500]">by @{it.by}</Link></span>}
                                </span>
                                <span className="text-[11px] text-[#6a6a6a] shrink-0">{it.at ? new Date(it.at).toLocaleDateString() : ""}</span>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
}

// Compact recent-activity preview for the overview open slot → full History tab.
function HistoryPreview({ onGo }) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get("/admin/activity").then((r) => setRows((r.data || []).slice(0, 6))).catch(() => setRows([]));
    }, []);
    return (
        <div className="border border-[#2a2a2a] bg-[#141414] p-3 sm:p-4 min-w-0" data-testid="history-preview">
            <button onClick={() => onGo("history")} data-testid="history-preview-open" className="w-full flex items-center justify-between text-[11px] uppercase tracking-[0.1em] text-[#a0a0a0] mb-2 hover:text-[#ff4500]">
                <span>Recent activity</span>
                <span className="inline-flex items-center gap-1 text-[#ff4500]">View all <ExternalLink className="h-3 w-3" /></span>
            </button>
            {rows === null ? <div className="text-sm text-[#666]">Loading…</div> : rows.length === 0 ? <div className="text-sm text-[#666]">No recent activity</div> : rows.map((r, i) => {
                return (
                    <div key={i} data-testid="history-preview-row" className="group flex items-start gap-2 py-2 border-b border-[#1a1a1a] last:border-0 text-sm">
                        <span className="flex flex-col min-w-0 flex-1 gap-0.5">
                            <span className="flex items-center gap-2 min-w-0">
                                <span className={`text-[9px] uppercase tracking-[0.12em] font-semibold w-16 shrink-0 ${ACTION_STYLE[r.action] || "text-[#a0a0a0]"}`}>{r.action}</span>
                                {r.link
                                    ? <Link to={r.link} target="_blank" rel="noopener noreferrer" className="text-white min-w-0 truncate hover:text-[#ff4500] transition-colors">{r.entity_name || r.entity_id}</Link>
                                    : <span className="text-white min-w-0 truncate">{r.entity_name || r.entity_id}</span>}
                            </span>
                            {r.actor_username && <span className="pl-[4.5rem] min-w-0"><Link to={`/u/${r.actor_username}`} target="_blank" rel="noopener noreferrer" data-testid="history-preview-by" className="text-[11px] text-[#6a6a6a] hover:text-[#ff4500]">by @{r.actor_username}</Link></span>}
                        </span>
                        <span className="text-[11px] text-[#6a6a6a] shrink-0">{r.at ? new Date(r.at).toLocaleDateString() : ""}</span>
                    </div>
                );
            })}
        </div>
    );
}

function OverviewDashboard({ onGo }) {
    const [d, setD] = useState(null);
    const [a2hs, setA2hs] = useState(null);
    const [metric, setMetric] = useState("signups");
    const [gran, setGran] = useState("day");
    const [to, setTo] = useState(isoDate(new Date()));
    const [from, setFrom] = useState(isoDate(new Date(Date.now() - 29 * 86400000)));
    useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
    useEffect(() => { api.get("/analytics/a2hs/summary").then((r) => setA2hs(r.data)).catch(() => setA2hs(null)); }, []);
    if (!d) return <Loading />;
    const kpis = [
        { k: "Users", v: d.users_total.toLocaleString(), icon: Users, c: "#fff" },
        { k: "New (30d)", v: d.signups_30d, icon: Users, c: "#22c55e" },
        { k: "Open flags", v: d.open_flags, icon: Flag, c: d.open_flags ? "#ef4444" : "#fff", sub: d.old_flags ? `${d.old_flags} over 24h` : "" },
        { k: "Suspended", v: d.suspended, icon: Ban, c: d.suspended ? "#f59e0b" : "#fff" },
    ];
    return (
        <div data-testid="admin-overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                {kpis.map((x) => {
                    const Icon = x.icon;
                    return (
                        <div key={x.k} className="border border-[#2a2a2a] bg-[#141414] p-4" data-testid={`kpi-${x.k}`}>
                            <div className="text-[11px] uppercase tracking-[0.1em] text-[#a0a0a0] flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {x.k}</div>
                            <div className="text-3xl font-bold mt-1" style={{ color: x.c }}>{x.v}</div>
                            {x.sub && <div className="text-[11px] text-[#ef4444] mt-0.5">{x.sub}</div>}
                        </div>
                    );
                })}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5 items-start min-w-0">
                <MetricsGraph totals={d.metric_totals || {}} metric={metric} setMetric={setMetric} gran={gran} setGran={setGran} from={from} setFrom={setFrom} to={to} setTo={setTo} />
                <MetricMap metric={metric} from={from} to={to} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
                <MetricDetail metric={metric} from={from} to={to} />
                <div className="border border-[#2a2a2a] bg-[#141414] p-3 sm:p-4 min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.1em] text-[#a0a0a0] mb-2">Content · latest counts</div>
                    {[["Teams", d.content.teams], ["Trainer listings", d.content.trainer_listings], ["Events", d.content.events], ["Reviews", d.content.reviews], ["Posts (7d)", d.content.posts_7d]].map(([label, n]) => (
                        <div key={label} className="flex items-center justify-between py-2 border-b border-[#1a1a1a] last:border-0 text-sm">
                            <span className="text-[#ddd]">{label}</span><span className="text-white font-semibold">{n}</span>
                        </div>
                    ))}
                </div>
                <NewestContent />
                <HistoryPreview onGo={onGo} />
            </div>
            {a2hs && (
                <div className="border border-[#2a2a2a] bg-[#141414] p-4 mt-5" data-testid="a2hs-analytics">
                    <div className="text-[11px] uppercase tracking-[0.1em] text-[#a0a0a0] mb-3 flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5 text-[#ff4500]" /> App install interest · "Add to Home Screen"</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                            ["Prompts shown", a2hs.by_event?.shown || 0, "#fff"],
                            ['"Add" taps', a2hs.by_event?.install_tap || 0, "#ff4500"],
                            ["Tap rate", `${a2hs.tap_rate || 0}%`, "#22c55e"],
                            ["Installed (Android)", a2hs.by_event?.installed || 0, "#22c55e"],
                        ].map(([label, val, color]) => (
                            <div key={label} className="border border-[#1a1a1a] bg-[#0f0f0f] p-3" data-testid={`a2hs-kpi-${label}`}>
                                <div className="text-[10px] uppercase tracking-[0.1em] text-[#8a8a8a]">{label}</div>
                                <div className="text-2xl font-bold mt-1" style={{ color }}>{val}</div>
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1.5 mt-3 text-xs">
                        {[
                            ["iOS taps", a2hs.install_by_platform?.ios || 0],
                            ["Android taps", a2hs.install_by_platform?.android || 0],
                            ["Maybe later", a2hs.by_event?.later || 0],
                            ["Don't show again", a2hs.by_event?.never || 0],
                            ["Declined (Android)", a2hs.by_event?.dismissed || 0],
                            ["Taps by logged-in users", a2hs.logged_in_taps || 0],
                        ].map(([label, val]) => (
                            <div key={label} className="flex items-center justify-between border-b border-[#1a1a1a] py-1">
                                <span className="text-[#a0a0a0]">{label}</span><span className="text-white font-semibold">{val}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
const ROLE_PILL = {
    team_director: "text-[#4aa3f0] bg-[#2196f3]/13 border-[#2196f3]/40",
    trainer: "text-[#4aa3f0] bg-[#2196f3]/13 border-[#2196f3]/40",
    scout: "text-[#a855f7] bg-[#a855f7]/13 border-[#a855f7]/40",
    player: "text-[#39d353] bg-[#22c55e]/13 border-[#22c55e]/40",
    fan: "text-[#a0a0a0] bg-[#1a1a1a] border-[#2a2a2a]",
};

const ROLE_OPTIONS = [["none", "None"], ["admin", "Admin"], ["content_admin", "Content Admin"], ["trusted_lister", "Trusted Lister"]];

function AccountsBrowser() {
    const { user: me } = useAuth();
    const meIsFullAdmin = !!me?.is_admin;
    const [q, setQ] = useState("");
    const [role, setRole] = useState("");
    const [status, setStatus] = useState("");
    const [data, setData] = useState(null);
    const [skip, setSkip] = useState(0);
    const [selected, setSelected] = useState(null);
    const limit = 25;

    const load = useCallback(() => {
        const p = new URLSearchParams({ skip: String(skip), limit: String(limit) });
        if (q.trim()) p.set("q", q.trim());
        if (role) p.set("role", role);
        if (status) p.set("status", status);
        api.get(`/admin/accounts?${p.toString()}`).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
    }, [q, role, status, skip]);
    useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

    if (selected) return <AccountDetail id={selected} onBack={() => { setSelected(null); load(); }} />;

    const roleOf = (u) => (u.is_admin ? "admin" : u.is_content_admin ? "content_admin" : u.is_trusted_lister ? "trusted_lister" : "none");
    const changeRole = async (u, value) => {
        if (value === roleOf(u)) return;
        const label = ROLE_OPTIONS.find(([v]) => v === value)?.[1] || value;
        if (!(await confirmAction({ title: "Change role?", message: `Set @${u.username}'s role to "${label}"?`, confirmText: "Change role" }))) { load(); return; }
        try { await api.post(`/admin/accounts/${u.id}/role`, { role: value }); toast.success(`Role changed to ${label}`); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed to change role"); load(); }
    };
    const rowSuspend = async (u) => {
        const val = u.status !== "suspended";
        if (!(await confirmAction({ title: val ? `Suspend @${u.username}?` : `Reactivate @${u.username}?`, message: val ? "They won't be able to log in until reactivated." : "They'll be able to log in again.", confirmText: val ? "Suspend" : "Reactivate" }))) return;
        try { await api.post(`/admin/accounts/${u.id}/suspend`, { suspend: val }); toast.success(val ? "Account suspended" : "Account reactivated"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const rowResetPw = async (u) => {
        if (!(await confirmAction({ title: `Reset @${u.username}'s password?`, message: "You'll enter a new password on the next step.", confirmText: "Continue" }))) return;
        const pw = window.prompt("Set a new password for this user (min 8 chars):");
        if (!pw) return;
        try { await api.post(`/admin/accounts/${u.id}/reset-password`, { password: pw }); toast.success("Password reset"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const rowDelete = async (u) => {
        if (!(await confirmAction({ title: `Delete @${u.username}?`, message: "This permanently deletes the account and their follows/notifications. This cannot be undone.", confirmText: "Delete account" }))) return;
        try { await api.delete(`/admin/accounts/${u.id}`); toast.success("Account deleted"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
    };

    return (
        <div data-testid="admin-accounts-browser">
            <div className="flex flex-wrap gap-2 mb-4">
                <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-[#0f0f0f] border border-[#2a2a2a] px-3">
                    <Search className="h-4 w-4 text-[#666]" />
                    <input data-testid="accounts-search" value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} placeholder="Search name, email, username…" className="flex-1 h-10 bg-transparent text-sm text-white outline-none" />
                </div>
                <select data-testid="accounts-role-filter" value={role} onChange={(e) => { setRole(e.target.value); setSkip(0); }} className="bg-[#0f0f0f] border border-[#2a2a2a] px-3 text-sm text-white outline-none">
                    <option value="">All roles</option><option value="standard">Standard</option><option value="pro">Pro</option><option value="content_admin">Content Admins</option><option value="trusted_lister">Trusted Listers</option>
                </select>
                <select data-testid="accounts-status-filter" value={status} onChange={(e) => { setStatus(e.target.value); setSkip(0); }} className="bg-[#0f0f0f] border border-[#2a2a2a] px-3 text-sm text-white outline-none">
                    <option value="">All status</option><option value="active">Active</option><option value="suspended">Suspended</option>
                </select>
            </div>
            {!data ? <Loading /> : (
                <>
                    <div className="border border-[#2a2a2a] overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr className="text-[10px] uppercase tracking-wider text-[#666] border-b border-[#2a2a2a]">
                                <th className="text-left p-3">User</th><th className="text-left p-3">Role</th><th className="text-left p-3">Type</th><th className="text-left p-3">Athlete</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th>
                            </tr></thead>
                            <tbody>
                                {data.items.map((u) => {
                                    const locked = u.is_admin || u.id === me?.id;
                                    return (
                                    <tr key={u.id} data-testid={`account-row-${u.id}`} className="border-b border-[#161616] hover:bg-[#101010]">
                                        <td className="p-3">
                                            <div className="flex items-center gap-2">
                                                <span className="h-7 w-7 rounded-full bg-[#1a1a1a] flex items-center justify-center text-xs text-[#ff4500] font-bold overflow-hidden shrink-0">{u.photo ? <img src={u.photo} alt="" className="h-full w-full object-cover" /> : (u.username || "?")[0].toUpperCase()}</span>
                                                <div><button onClick={() => setSelected(u.id)} data-testid={`account-open-${u.id}`} className="text-white hover:text-[#ff4500] text-left">{u.username}</button><div className="text-[11px] text-[#666]">{u.email}</div></div>
                                            </div>
                                        </td>
                                        <td className="p-3">
                                            <select
                                                data-testid={`account-role-select-${u.id}`}
                                                value={roleOf(u)}
                                                disabled={locked}
                                                onChange={(e) => changeRole(u, e.target.value)}
                                                title={locked ? "Protected account" : "Assign role"}
                                                className="bg-[#0f0f0f] border border-[#2a2a2a] h-8 px-2 text-xs text-white outline-none rounded-none disabled:opacity-50 disabled:cursor-not-allowed focus:border-[#a855f7]"
                                            >
                                                {ROLE_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                                            </select>
                                        </td>
                                        <td className="p-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${u.is_pro ? "text-[#f5b301] bg-[#f5b301]/13 border-[#f5b301]/40" : "text-[#a0a0a0] bg-[#2a2a2a]/40 border-[#3a3a3a]"}`} data-testid={`account-type-${u.id}`}>{u.is_pro ? "Pro" : "Standard"}</span></td>
                                        <td className="p-3">{u.athlete ? <a href={`/players/${u.athlete.id}`} target="_blank" rel="noreferrer" data-testid={`account-athlete-${u.id}`} className="text-[#a855f7] hover:underline inline-flex items-center gap-1">{u.athlete.name || "View"} <ExternalLink className="h-3 w-3 opacity-60" /></a> : <span className="text-[#555]">—</span>}</td>
                                        <td className="p-3">{u.status === "suspended" ? <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border text-[#ef4444] bg-[#ef4444]/13 border-[#ef4444]/40">Suspended</span> : <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border text-[#22c55e] bg-[#22c55e]/13 border-[#22c55e]/40">Active</span>}</td>
                                        <td className="p-3">
                                            <div className="flex items-center justify-end gap-1.5">
                                                <button onClick={() => rowResetPw(u)} disabled={u.is_admin} title="Reset password" data-testid={`account-reset-pw-${u.id}`} className="h-8 w-8 inline-flex items-center justify-center border border-[#2a2a2a] text-[#a0a0a0] hover:border-[#ff4500] hover:text-[#ff4500] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"><KeyRound className="h-4 w-4" /></button>
                                                <button onClick={() => rowSuspend(u)} disabled={u.is_admin} title={u.status === "suspended" ? "Reactivate account" : "Suspend account"} data-testid={`account-suspend-${u.id}`} className={`h-8 w-8 inline-flex items-center justify-center border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${u.status === "suspended" ? "border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10" : "border-[#2a2a2a] text-[#a0a0a0] hover:border-red-500 hover:text-red-500"}`}>{u.status === "suspended" ? <Check className="h-4 w-4" /> : <Ban className="h-4 w-4" />}</button>
                                                {meIsFullAdmin && <button onClick={() => rowDelete(u)} disabled={u.is_admin} title="Delete account" data-testid={`account-delete-${u.id}`} className="h-8 w-8 inline-flex items-center justify-center border border-[#2a2a2a] text-[#a0a0a0] hover:border-red-500 hover:text-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 className="h-4 w-4" /></button>}
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex items-center justify-between mt-3 text-xs text-[#a0a0a0]">
                        <span>{data.total} users</span>
                        <div className="flex gap-2">
                            <Button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="rounded-none bg-transparent border border-[#2a2a2a] h-8 text-xs disabled:opacity-40">Prev</Button>
                            <Button disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)} className="rounded-none bg-transparent border border-[#2a2a2a] h-8 text-xs disabled:opacity-40">Next</Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function AccountDetail({ id, onBack }) {
    const { user: me } = useAuth();
    const meIsFullAdmin = !!me?.is_admin;
    const [d, setD] = useState(null);
    const load = useCallback(() => { api.get(`/admin/accounts/${id}`).then((r) => setD(r.data)).catch(() => setD(null)); }, [id]);
    useEffect(() => { load(); }, [load]);

    const suspend = async (val) => {
        try { await api.post(`/admin/accounts/${id}/suspend`, { suspend: val }); toast.success(val ? "Account suspended" : "Account reactivated"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const setContentAdmin = async (val) => {
        try { await api.post(`/admin/accounts/${id}/content-admin`, { enable: val }); toast.success(val ? "Content Admin granted" : "Content Admin removed"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const setTrustedLister = async (val) => {
        try { await api.post(`/admin/accounts/${id}/trusted-lister`, { enable: val }); toast.success(val ? "Trusted Lister granted" : "Trusted Lister removed"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const resetPw = async () => {
        const pw = window.prompt("Set a new password for this user (min 8 chars):");
        if (!pw) return;
        try { await api.post(`/admin/accounts/${id}/reset-password`, { password: pw }); toast.success("Password reset"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const deleteAccount = async () => {
        if (!(await confirmAction({ title: `Delete @${d?.username}?`, message: "This permanently deletes the user account and their follows/notifications. This cannot be undone.", confirmText: "Delete account" }))) return;
        try { await api.delete(`/admin/accounts/${id}`); toast.success("Account deleted"); onBack(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
    };

    if (!d) return <Loading />;
    return (
        <div data-testid="admin-account-detail">
            <button onClick={onBack} data-testid="account-detail-back" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#a0a0a0] hover:text-[#ff4500] mb-4"><ArrowLeft className="h-3.5 w-3.5" /> Back to accounts</button>
            <div className="flex items-start justify-between gap-4 flex-wrap border border-[#2a2a2a] bg-[#141414] p-5">
                <div className="flex items-center gap-3">
                    <span className="h-12 w-12 rounded-full bg-[#1a1a1a] flex items-center justify-center text-lg text-[#ff4500] font-bold overflow-hidden">{d.photo ? <img src={d.photo} alt="" className="h-full w-full object-cover" /> : (d.username || "?")[0].toUpperCase()}</span>
                    <div>
                        <Link to={`/u/${d.username}`} className="text-xl font-bold text-white hover:text-[#ff4500]">{d.username}</Link>
                        <div className="text-sm text-[#a0a0a0]">{d.name || "—"} · {d.email}</div>
                        <div className="text-[11px] mt-1"><span className={`uppercase font-bold px-2 py-0.5 rounded-full border ${ROLE_PILL[d.role] || ROLE_PILL.fan}`}>{d.role.replace("_", " ")}</span> {d.is_content_admin && <span className="ml-1 uppercase font-bold px-2 py-0.5 rounded-full border text-[#a855f7] bg-[#a855f7]/13 border-[#a855f7]/40">Content Admin</span>} {d.is_trusted_lister && <span className="ml-1 uppercase font-bold px-2 py-0.5 rounded-full border text-[#38bdf8] bg-[#38bdf8]/13 border-[#38bdf8]/40">Trusted Lister</span>} {d.status === "suspended" ? <span className="ml-1 text-[#ef4444]">Suspended</span> : <span className="ml-1 text-[#22c55e]">Active</span>}</div>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {meIsFullAdmin && !d.is_admin && (
                        d.is_content_admin ? (
                            <Button onClick={() => setContentAdmin(false)} data-testid="account-remove-content-admin" className="rounded-none bg-transparent border border-[#a855f7] text-[#a855f7] hover:bg-[#a855f7]/10 uppercase tracking-wider text-[10px] h-9"><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Remove Content Admin</Button>
                        ) : (
                            <Button onClick={() => setContentAdmin(true)} data-testid="account-make-content-admin" className="rounded-none bg-transparent border border-[#2a2a2a] hover:border-[#a855f7] hover:text-[#a855f7] uppercase tracking-wider text-[10px] h-9"><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Make Content Admin</Button>
                        )
                    )}
                    {meIsFullAdmin && !d.is_admin && (
                        d.is_trusted_lister ? (
                            <Button onClick={() => setTrustedLister(false)} data-testid="account-remove-trusted-lister" className="rounded-none bg-transparent border border-[#38bdf8] text-[#38bdf8] hover:bg-[#38bdf8]/10 uppercase tracking-wider text-[10px] h-9"><Check className="h-3.5 w-3.5 mr-1" /> Remove Trusted Lister</Button>
                        ) : (
                            <Button onClick={() => setTrustedLister(true)} data-testid="account-make-trusted-lister" className="rounded-none bg-transparent border border-[#2a2a2a] hover:border-[#38bdf8] hover:text-[#38bdf8] uppercase tracking-wider text-[10px] h-9"><Check className="h-3.5 w-3.5 mr-1" /> Make Trusted Lister</Button>
                        )
                    )}
                    {!d.is_admin && <Button onClick={resetPw} data-testid="account-reset-pw" className="rounded-none bg-transparent border border-[#2a2a2a] hover:border-[#ff4500] uppercase tracking-wider text-[10px] h-9"><KeyRound className="h-3.5 w-3.5 mr-1" /> Reset password</Button>}
                    {d.is_admin ? (
                        <span className="text-[11px] text-[#666] self-center">Protected admin</span>
                    ) : d.status === "suspended" ? (
                        <Button onClick={() => suspend(false)} data-testid="account-reactivate" className="rounded-none bg-[#22c55e] hover:bg-[#16a34a] uppercase tracking-wider text-[10px] h-9"><Check className="h-3.5 w-3.5 mr-1" /> Reactivate</Button>
                    ) : (
                        <Button onClick={() => suspend(true)} data-testid="account-suspend" className="rounded-none bg-red-600 hover:bg-red-500 uppercase tracking-wider text-[10px] h-9"><Ban className="h-3.5 w-3.5 mr-1" /> Suspend</Button>
                    )}
                    {meIsFullAdmin && !d.is_admin && (
                        <Button onClick={deleteAccount} data-testid="account-delete" className="rounded-none bg-transparent border border-red-600 text-red-500 hover:bg-red-600 hover:text-white uppercase tracking-wider text-[10px] h-9"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete account</Button>
                    )}
                </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="border border-[#2a2a2a] bg-[#141414] p-4">
                    <div className="text-[11px] uppercase tracking-[0.1em] text-[#a0a0a0] mb-2">Details</div>
                    {[["Followers", d.followers], ["Following", d.following], ["Thanks", d.thanks_count], ["Profiles", (d.profiles || []).join(", ") || "fan"], ["Joined", d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"]].map(([k, v]) => (
                        <div key={k} className="flex justify-between py-1.5 border-b border-[#1a1a1a] last:border-0 text-sm"><span className="text-[#a0a0a0]">{k}</span><span className="text-white">{v}</span></div>
                    ))}
                </div>
                <div className="border border-[#2a2a2a] bg-[#141414] p-4">
                    <div className="text-[11px] uppercase tracking-[0.1em] text-[#a0a0a0] mb-2">Owned teams ({d.teams.length})</div>
                    {d.teams.length === 0 ? <div className="text-sm text-[#666]">None</div> : d.teams.map((t) => (
                        <Link key={t.id} to={`/teams/${t.id}`} className="flex justify-between py-1.5 border-b border-[#1a1a1a] last:border-0 text-sm hover:text-[#ff4500]"><span>{t.name}</span>{t.owner_verified ? <span className="text-[#22c55e] text-xs">verified</span> : <span className="text-[#f59e0b] text-xs">pending</span>}</Link>
                    ))}
                    <div className="text-[11px] uppercase tracking-[0.1em] text-[#a0a0a0] mt-3 mb-1">Recent posts</div>
                    {d.recent_posts.length === 0 ? <div className="text-sm text-[#666]">None</div> : d.recent_posts.map((p) => (
                        <div key={p.id} className="text-sm text-[#cfcfcf] truncate py-1 border-b border-[#1a1a1a] last:border-0">{p.body || "(media)"}</div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ============================ Content manager ============================
const CONTENT_TABS = [
    { key: "teams", label: "Teams" },
    { key: "trainers", label: "Trainers" },
    { key: "events", label: "Events" },
    { key: "athletes", label: "Athletes" },
    { key: "reviews", label: "Reviews" },
];

// Multi-select sport dropdown. Empty selection = all sports.
function SportMultiSelect({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);
    const toggle = (s) => onChange(value.includes(s) ? value.filter((x) => x !== s) : [...value, s]);
    const label = value.length === 0 ? "All sports" : value.length === 1 ? value[0] : `${value.length} sports`;
    return (
        <div className="relative" ref={ref} data-testid="admin-content-sport-filter">
            <button
                type="button"
                data-testid="admin-sport-dropdown-btn"
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-2 h-9 px-3 text-xs uppercase tracking-wider border border-[#2a2a2a] text-white bg-[#141414] hover:border-[#ff4500]"
            >
                <Dumbbell className="h-3.5 w-3.5 text-[#ff4500]" />
                <span>{label}</span>
                <ChevronDown className="h-3.5 w-3.5 text-[#a0a0a0]" />
            </button>
            {open && (
                <div className="absolute z-50 mt-1 w-56 max-h-72 overflow-y-auto bg-[#0f0f0f] border border-[#2a2a2a] shadow-xl p-1" data-testid="admin-sport-dropdown-panel">
                    <button
                        type="button"
                        data-testid="admin-sport-opt-all"
                        onClick={() => onChange([])}
                        className={`w-full text-left px-3 py-2 text-xs uppercase tracking-wider flex items-center gap-2 ${value.length === 0 ? "text-[#ff4500]" : "text-[#cfcfcf] hover:bg-[#1a1a1a]"}`}
                    >
                        <span className="w-3.5 inline-flex">{value.length === 0 ? <Check className="h-3.5 w-3.5" /> : null}</span>
                        All sports
                    </button>
                    {SPORTS.map((s) => {
                        const on = value.includes(s);
                        return (
                            <button
                                key={s}
                                type="button"
                                data-testid={`admin-sport-opt-${s}`}
                                onClick={() => toggle(s)}
                                className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 ${on ? "text-[#ff4500]" : "text-[#cfcfcf] hover:bg-[#1a1a1a]"}`}
                            >
                                <span className="w-3.5 inline-flex">{on ? <Check className="h-3.5 w-3.5" /> : null}</span>
                                {s}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Multi-select content-type dropdown (Teams/Trainers/Events/Athletes/Reviews).
function ContentTypeMultiSelect({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);
    const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
    const allKeys = CONTENT_TABS.map((t) => t.key);
    const allOn = value.length === allKeys.length;
    const label = value.length === 0 ? "Select types" : allOn ? "All types" : value.length === 1 ? (CONTENT_TABS.find((t) => t.key === value[0])?.label || "1 selected") : `${value.length} selected`;
    return (
        <div className="relative" ref={ref} data-testid="admin-content-type-filter">
            <button
                type="button"
                data-testid="content-type-dropdown-btn"
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-2 h-9 px-3 text-xs uppercase tracking-wider border border-[#2a2a2a] text-white bg-[#141414] hover:border-[#ff4500]"
            >
                <Database className="h-3.5 w-3.5 text-[#ff4500]" />
                <span>{label}</span>
                <ChevronDown className="h-3.5 w-3.5 text-[#a0a0a0]" />
            </button>
            {open && (
                <div className="absolute z-50 mt-1 w-56 bg-[#0f0f0f] border border-[#2a2a2a] shadow-xl p-1" data-testid="content-type-dropdown-panel">
                    <button
                        type="button"
                        data-testid="content-type-opt-all"
                        onClick={() => onChange(allOn ? [] : allKeys)}
                        className={`w-full text-left px-3 py-2 text-xs uppercase tracking-wider flex items-center gap-2 ${allOn ? "text-[#ff4500]" : "text-[#cfcfcf] hover:bg-[#1a1a1a]"}`}
                    >
                        <span className="w-3.5 inline-flex">{allOn ? <Check className="h-3.5 w-3.5" /> : null}</span>
                        All types
                    </button>
                    {CONTENT_TABS.map((t) => {
                        const on = value.includes(t.key);
                        return (
                            <button
                                key={t.key}
                                type="button"
                                data-testid={`content-type-opt-${t.key}`}
                                onClick={() => toggle(t.key)}
                                className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 ${on ? "text-[#ff4500]" : "text-[#cfcfcf] hover:bg-[#1a1a1a]"}`}
                            >
                                <span className="w-3.5 inline-flex">{on ? <Check className="h-3.5 w-3.5" /> : null}</span>
                                {t.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function DuplicateTeamsPanel() {
    const [groups, setGroups] = useState(null);
    const [open, setOpen] = useState(false);
    const [keep, setKeep] = useState({}); // groupIdx -> keep id
    const [busy, setBusy] = useState("");

    const load = useCallback(() => {
        api.get("/admin/teams/duplicates").then((r) => {
            const g = r.data.groups || [];
            setGroups(g);
            setKeep(Object.fromEntries(g.map((grp, i) => [i, grp.suggested_keep])));
        }).catch(() => setGroups([]));
    }, []);
    useEffect(() => { load(); }, [load]);

    const dedupe = async (grp, idx) => {
        const keepId = keep[idx] || grp.suggested_keep;
        const removeIds = grp.members.map((m) => m.id).filter((id) => id !== keepId);
        if (!removeIds.length) return;
        setBusy(String(idx));
        try {
            const { data } = await api.post("/admin/teams/dedupe", { keep_id: keepId, remove_ids: removeIds });
            toast.success(`Removed ${data.removed} duplicate${data.removed === 1 ? "" : "s"} — moved ${data.moved.reviews} reviews, ${data.moved.follows} follows`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Cleanup failed"); }
        finally { setBusy(""); }
    };

    if (!groups) return null;
    if (groups.length === 0) {
        return (
            <div className="mb-4 border border-[#1f1f1f] bg-[#0f0f0f] px-4 py-3 text-xs text-[#8a8a8a]" data-testid="dup-none">
                No duplicate teams found — every listing is unique by name, age group, gender and state.
            </div>
        );
    }
    return (
        <div className="mb-5 border border-[#f59e0b]/40 bg-[#f59e0b]/5" data-testid="dup-panel">
            <button onClick={() => setOpen((v) => !v)} data-testid="dup-toggle" className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left">
                <span className="inline-flex items-center gap-2 text-sm text-[#f59e0b]">
                    <AlertTriangle className="h-4 w-4" /> {groups.length} duplicate group{groups.length === 1 ? "" : "s"} detected
                </span>
                <ChevronDown className={`h-4 w-4 text-[#f59e0b] transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="px-4 pb-4 space-y-3">
                    {groups.map((grp, idx) => (
                        <div key={idx} className="border border-[#2a2a2a] bg-[#141414] p-3" data-testid={`dup-group-${idx}`}>
                            <div className="text-[11px] uppercase tracking-[0.15em] text-[#a0a0a0] mb-2">
                                {grp.key.name} · {grp.key.age_group || "—"} · {grp.key.gender || "—"} · {grp.key.state || "—"} · {grp.count} copies
                            </div>
                            <div className="space-y-1.5">
                                {grp.members.map((m) => (
                                    <label key={m.id} data-testid={`dup-member-${m.id}`} className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input type="radio" name={`keep-${idx}`} checked={(keep[idx] || grp.suggested_keep) === m.id} onChange={() => setKeep((k) => ({ ...k, [idx]: m.id }))} className="accent-[#22c55e]" data-testid={`dup-keep-${m.id}`} />
                                        <span className="text-white">{m.city || "—"}, {m.state}</span>
                                        <span className="text-[11px] text-[#8a8a8a]">{m.claimed ? "claimed" : "unclaimed"} · {m.ref_reviews}★ · {m.ref_follows} followers · {m.is_seed ? "seed" : "user-added"}</span>
                                        {(keep[idx] || grp.suggested_keep) === m.id && <span className="text-[10px] uppercase tracking-wider text-[#22c55e]">keep</span>}
                                    </label>
                                ))}
                            </div>
                            <Button data-testid={`dup-merge-${idx}`} disabled={busy === String(idx)} onClick={() => dedupe(grp, idx)}
                                className="mt-3 rounded-none bg-red-600 hover:bg-red-500 text-[11px] uppercase tracking-wider h-8">
                                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove {grp.count - 1} duplicate{grp.count - 1 === 1 ? "" : "s"}, keep selected
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ContentManager() {
    // Sport filter (multi; empty = all) + one search box that filters every selected content type.
    const [sports, setSports] = useState([]);
    const [subs, setSubs] = useState(["teams", "trainers", "events", "athletes", "reviews"]);
    const [query, setQuery] = useState("");
    const [source, setSource] = useState("all"); // all | seed | user
    const Section = ({ title, children }) => (
        <div className="mb-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#ff4500] mb-2 border-b border-[#2a2a2a] pb-1">{title}</div>
            {children}
        </div>
    );
    return (
        <div data-testid="admin-content-manager">
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <SportMultiSelect value={sports} onChange={setSports} />
                <span className="w-px h-6 bg-[#2a2a2a] mx-1" />
                <ContentTypeMultiSelect value={subs} onChange={setSubs} />
            </div>
            <div className="flex items-center gap-2 bg-[#0f0f0f] border border-[#2a2a2a] px-3 mb-5">
                <Search className="h-4 w-4 text-[#666]" />
                <input
                    data-testid="admin-content-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search selected listings & reviews by name…"
                    className="flex-1 h-11 bg-transparent text-sm text-white outline-none"
                />
                {query && <button data-testid="admin-content-search-clear" onClick={() => setQuery("")} className="text-[#666] hover:text-white"><X className="h-4 w-4" /></button>}
            </div>
            <div className="flex items-center gap-2 mb-5" data-testid="admin-source-filter">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#8a8a8a]">Source</span>
                {[["all", "All"], ["seed", "Seed"], ["user", "User-added"]].map(([k, l]) => (
                    <button key={k} data-testid={`source-filter-${k}`} onClick={() => setSource(k)}
                        className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] border ${source === k ? "border-[#ff4500] text-white bg-[#ff4500]/10" : "border-[#2a2a2a] text-[#a0a0a0] hover:text-white"}`}>{l}</button>
                ))}
            </div>
            {subs.length === 0 && (
                <div className="border border-[#2a2a2a] p-8 text-center text-[#a0a0a0]" data-testid="admin-content-empty">Select at least one content type above.</div>
            )}
            {subs.includes("teams") && <Section title="Teams"><DuplicateTeamsPanel /><TeamsManager sports={sports} query={query} source={source} /></Section>}
            {subs.includes("trainers") && <Section title="Trainers"><SimpleListManager kind="trainer-listings" testid="trainers" sports={sports} query={query} source={source} /></Section>}
            {subs.includes("events") && <Section title="Events"><SimpleListManager kind="events" testid="events" sports={sports} query={query} source={source} /></Section>}
            {subs.includes("athletes") && <Section title="Athletes"><AthletesManager query={query} source={source} /></Section>}
            {subs.includes("reviews") && <Section title="Reviews"><ReviewsModeration sports={sports} query={query} /></Section>}
        </div>
    );
}

function SimpleListManager({ kind, testid, sports = [], query = "", source = "all" }) {
    const [items, setItems] = useState(null);
    const load = useCallback(() => {
        api.get(`/admin/${kind}`).then((r) => setItems(r.data)).catch(() => setItems([]));
    }, [kind]);
    useEffect(() => { load(); }, [load]);

    const q = query.trim().toLowerCase();
    const visible = (items || []).filter((it) => {
        if (sports.length && !sports.includes(it.sport)) return false;
        if (source === "seed" && !it.is_seed) return false;
        if (source === "user" && it.is_seed) return false;
        if (!q) return true;
        return (it.name || "").toLowerCase().includes(q)
            || (it.city || "").toLowerCase().includes(q)
            || (it.state || "").toLowerCase().includes(q);
    });
    return (
        <div data-testid={`admin-${testid}-manager`}>
            {!items ? <Loading /> : visible.length === 0 ? <Empty label="Nothing found" testid={`admin-${testid}-empty`} /> : (
                <div className="space-y-2">
                    {visible.map((it) => (
                        <AdminContentRow key={it.id} kind={kind} item={it} onChange={load} />
                    ))}
                </div>
            )}
        </div>
    );
}

function AthletesManager({ query = "", source = "all" }) {
    const [items, setItems] = useState(null);
    const load = useCallback(() => {
        api.get("/admin/players").then((r) => setItems(r.data)).catch(() => setItems([]));
    }, []);
    useEffect(() => { load(); }, [load]);

    const remove = async (it) => {
        const msg = it.is_example
            ? `Delete the EXAMPLE athlete "${it.name}"? This removes the sample profile entirely. This can't be undone.`
            : `Delete the athlete profile for "${it.name}"? This strips their athlete listing (position, grad year, visibility, bio, photo). The user account stays. This can't be undone.`;
        if (!window.confirm(msg)) return;
        try { await api.delete(`/admin/players/${it.id}`); toast.success(it.is_example ? "Example profile deleted" : "Athlete profile deleted"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    const toggleVisible = async (it) => {
        const next = !it.visible;
        try {
            await api.post(`/admin/players/${it.id}/visibility`, { visible: next });
            toast.success(next ? "Profile is now visible" : "Profile hidden");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not update visibility"); }
    };

    const q = query.trim().toLowerCase();
    const visible = (items || []).filter((it) => {
        if (source === "seed" && !it.is_seed) return false;
        if (source === "user" && it.is_seed) return false;
        if (!q) return true;
        return (it.name || "").toLowerCase().includes(q)
            || (it.email || "").toLowerCase().includes(q)
            || (it.username || "").toLowerCase().includes(q);
    });

    return (
        <div data-testid="admin-athletes-manager">
            {!items ? <Loading /> : visible.length === 0 ? <Empty label="No athlete profiles found" testid="admin-athletes-empty" /> : (
                <div className="space-y-2">
                    {visible.map((it) => (
                        <div key={it.id} data-testid={`admin-athlete-${it.id}`} className="border border-[#2a2a2a] bg-[#141414] px-3 py-2 flex items-center gap-3">
                            <span className="h-8 w-8 rounded-full bg-[#1a1a1a] flex items-center justify-center text-xs text-[#a855f7] font-bold overflow-hidden shrink-0">{it.photo ? <img src={it.photo} alt="" className="h-full w-full object-cover" /> : (it.name || "?")[0].toUpperCase()}</span>
                            <a href={`/players/${it.id}`} target="_blank" rel="noreferrer" data-testid={`admin-athlete-name-${it.id}`} className="font-display text-base tracking-wide text-white hover:text-[#ff4500] inline-flex items-center gap-1 shrink-0 max-w-[12rem] truncate">
                                <span className="truncate">{it.name}</span><ExternalLink className="h-3 w-3 opacity-60 shrink-0" />
                            </a>
                            {it.is_example && <span className="bg-[#a855f7] text-white text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm shrink-0" data-testid={`admin-athlete-example-${it.id}`}>Example</span>}
                            <div className="text-[11px] text-[#8a8a8a] truncate flex-1 min-w-0">
                                {[it.position, it.grad_year ? `'${String(it.grad_year).slice(-2)}` : null, it.home_zip].filter(Boolean).join(" · ")}
                                {(it.position || it.grad_year || it.home_zip) ? " · " : ""}
                                {it.is_example ? <span className="text-[#a855f7] uppercase">example</span> : it.is_seed ? <span className="uppercase">seed</span> : <span className="text-[#4aa3f0] uppercase">user</span>}
                                {it.status === "suspended" ? <span className="text-[#ef4444] uppercase"> · susp</span> : null}
                                <span className="text-[#5a5a5a]"> · {it.email}{it.username ? ` · @${it.username}` : ""}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => toggleVisible(it)}
                                    title={it.visible ? "Visible — click to hide this profile" : "Hidden — click to make visible"}
                                    data-testid={`admin-athlete-visible-${it.id}`}
                                    className={`text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded-sm transition-colors cursor-pointer ${it.visible ? "bg-[#16a34a] text-white hover:bg-[#12813a]" : "bg-[#2a2a2a] text-[#a0a0a0] hover:bg-[#3a3a3a] hover:text-white"}`}
                                >{it.visible ? "Visible" : "Hidden"}</button>
                                <button
                                    type="button"
                                    onClick={() => remove(it)}
                                    title="Delete profile"
                                    data-testid={`admin-delete-athlete-${it.id}`}
                                    className="h-8 w-8 inline-flex items-center justify-center border border-[#2a2a2a] text-[#a0a0a0] hover:border-red-500 hover:text-red-500 transition-colors"
                                ><Trash2 className="h-4 w-4" /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ImageModeration() {
    const [status, setStatus] = useState("open");
    const [data, setData] = useState({ items: [], counts: {} });
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/image-moderation?status=${status}`);
            setData(data);
        } finally { setLoading(false); }
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const act = async (id, action, body) => {
        try {
            await api.post(`/admin/image-moderation/${id}/${action}`, body || {});
            toast.success(action === "email" ? "Email sent" : `Done: ${action}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    };

    const TABS2 = [["open", "Flagged Queue"], ["resolved", "Resolved"]];
    return (
        <div data-testid="admin-image-moderation">
            <div className="flex items-center gap-4 mb-4">
                {TABS2.map(([k, l]) => (
                    <button key={k} data-testid={`mod-filter-${k}`} onClick={() => setStatus(k)}
                        className={`text-xs uppercase tracking-[0.2em] pb-1 border-b-2 ${status === k ? "border-[#ff4500] text-white" : "border-transparent text-[#a0a0a0]"}`}>
                        {l}{k === "open" && data.counts?.open ? ` (${data.counts.open})` : ""}
                    </button>
                ))}
            </div>
            {loading ? <div className="text-[#a0a0a0] py-10 text-center">Loading…</div> :
                data.items.length === 0 ? <div className="border border-[#2a2a2a] p-8 text-center text-[#a0a0a0]" data-testid="mod-empty">Nothing here.</div> :
                <div className="space-y-4">
                    {data.items.map((r) => {
                        const locked = r.status === "locked";
                        const img = r.pending_url || r.real_url;
                        return (
                            <div key={r.id} data-testid={`mod-card-${r.id}`} className="flex gap-4 border border-[#2a2a2a] bg-[#111] p-4">
                                <div className="w-[140px] h-[140px] shrink-0 border border-[#2a2a2a] bg-[#1a1a1a] overflow-hidden relative">
                                    <img src={img} alt="flagged" className="w-full h-full object-cover" style={{ filter: "blur(7px)" }} />
                                    <span className="absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-[0.15em] text-[#ffb4b4] text-center px-2">Flagged image (blurred)</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border ${locked ? "text-red-400 border-red-500 bg-red-500/10" : "text-amber-400 border-amber-500 bg-amber-500/10"}`}>
                                            {locked ? "Account locked" : "Pending review · listing live"}
                                        </span>
                                        <span className="text-[11px] text-[#ffb4b4]">{r.top_category} · {r.max_score}</span>
                                        {r.status === "allowed" && <span className="text-[11px] text-green-400">Allowed</span>}
                                        {r.status === "deleted" && <span className="text-[11px] text-[#a0a0a0]">Deleted</span>}
                                    </div>
                                    <div className="text-xs text-[#a0a0a0] mt-2 leading-relaxed">
                                        <b className="text-[#ddd]">User:</b> {r.user_name} · {r.user_email} · {r.user_username}<br />
                                        <b className="text-[#ddd]">Uploaded:</b> {new Date(r.created_at).toLocaleString()}
                                    </div>
                                    <textarea data-testid={`mod-note-${r.id}`} value={notes[r.id] ?? r.admin_note ?? ""} onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                                        placeholder="Admin note (reason for suspension)…" className="w-full mt-3 bg-[#0a0a0a] border border-[#2a2a2a] text-sm p-2 min-h-[52px]" />
                                    <div className="flex gap-2 mt-3 flex-wrap">
                                        <button data-testid={`mod-allow-${r.id}`} onClick={() => act(r.id, "allow")} className="text-[11px] uppercase tracking-[0.15em] px-3 py-2 border border-green-500 text-green-400 hover:bg-green-500/10">✓ Allow image</button>
                                        <button data-testid={`mod-delete-${r.id}`} onClick={() => act(r.id, "delete")} className="text-[11px] uppercase tracking-[0.15em] px-3 py-2 border border-[#2a2a2a] text-[#ddd] hover:border-[#555]">🗑 Delete image</button>
                                        <button data-testid={`mod-suspend-${r.id}`} onClick={() => act(r.id, "suspend", { note: notes[r.id] || "" })} className="text-[11px] uppercase tracking-[0.15em] px-3 py-2 bg-red-500 border border-red-500 text-white hover:bg-red-600">⦸ Suspend / Ban</button>
                                        <button data-testid={`mod-reinstate-${r.id}`} onClick={() => act(r.id, "reinstate")} className="text-[11px] uppercase tracking-[0.15em] px-3 py-2 border border-[#2a2a2a] text-[#a0a0a0] hover:border-[#555]">↺ Reinstate</button>
                                        <button data-testid={`mod-email-${r.id}`} onClick={() => act(r.id, "email", { message: notes[r.id] || "" })} className="text-[11px] uppercase tracking-[0.15em] px-3 py-2 border border-[#2196f3] text-[#2196f3] hover:bg-[#2196f3]/10">✉ Email user</button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>}
        </div>
    );
}

function SuspendedUsers() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/suspended-users"); setItems(data.items || []); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const act = async (id, action) => {
        try {
            await api.post(`/admin/users/${id}/${action}`, action === "ban" ? { note: "Banned by admin" } : {});
            toast.success(action === "reinstate" ? "Account reinstated" : "Account banned");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    };

    if (loading) return <div className="text-[#a0a0a0] py-10 text-center">Loading…</div>;
    if (items.length === 0) return <div className="border border-[#2a2a2a] p-8 text-center text-[#a0a0a0]" data-testid="suspended-empty">No suspended or banned accounts.</div>;
    return (
        <div className="space-y-3" data-testid="admin-suspended-users">
            {items.map((u) => (
                <div key={u.id} data-testid={`suspended-user-${u.id}`} className="flex items-center gap-4 border border-[#2a2a2a] bg-[#111] p-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display text-lg tracking-wide">{u.name}</span>
                            <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border ${u.status === "banned" ? "text-red-400 border-red-500 bg-red-500/10" : "text-amber-400 border-amber-500 bg-amber-500/10"}`}>{u.status}</span>
                        </div>
                        <div className="text-xs text-[#a0a0a0] mt-1">{u.email} · {u.username}</div>
                        {u.suspended_reason && <div className="text-xs text-[#ffb4b4] mt-1"><b className="text-[#ddd]">Reason:</b> {u.suspended_reason}</div>}
                        {u.suspended_at && <div className="text-[10px] text-[#666] mt-0.5">{new Date(u.suspended_at).toLocaleString()}{u.suspended_by ? ` · by ${u.suspended_by}` : ""}</div>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <button data-testid={`reinstate-${u.id}`} onClick={() => act(u.id, "reinstate")} className="text-[11px] uppercase tracking-[0.15em] px-3 py-2 border border-green-500 text-green-400 hover:bg-green-500/10">↺ Reinstate</button>
                        {u.status !== "banned" && <button data-testid={`ban-${u.id}`} onClick={() => act(u.id, "ban")} className="text-[11px] uppercase tracking-[0.15em] px-3 py-2 bg-red-500 border border-red-500 text-white hover:bg-red-600">⦸ Ban</button>}
                    </div>
                </div>
            ))}
        </div>
    );
}


const FR_STATUS_META = {
    open: { label: "Open", cls: "text-[#ff4500] border-[#ff4500]/40 bg-[#ff4500]/10" },
    planned: { label: "Planned", cls: "text-[#3b82f6] border-[#3b82f6]/40 bg-[#3b82f6]/10" },
    in_progress: { label: "In progress", cls: "text-[#eab308] border-[#eab308]/40 bg-[#eab308]/10" },
    done: { label: "Done", cls: "text-[#22c55e] border-[#22c55e]/40 bg-[#22c55e]/10" },
    declined: { label: "Declined", cls: "text-[#a0a0a0] border-[#333] bg-white/5" },
};

function FeatureRequestsQueue() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/feature-requests"); setRows(data || []); }
        catch { toast.error("Could not load feature requests"); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/feature-requests/${id}`, { status });
            setRows((r) => r.map((x) => x.id === id ? { ...x, status } : x));
        } catch { toast.error("Could not update"); }
    };
    const remove = async (id) => {
        if (!(await confirmAction({ title: "Delete request?", message: "This will permanently delete this feature request.", confirmText: "Delete" }))) return;
        try { await api.delete(`/admin/feature-requests/${id}`); setRows((r) => r.filter((x) => x.id !== id)); }
        catch { toast.error("Could not delete"); }
    };

    const shown = filter === "all" ? rows : rows.filter((r) => (r.status || "open") === filter);
    const counts = rows.reduce((a, r) => { const s = r.status || "open"; a[s] = (a[s] || 0) + 1; return a; }, {});

    return (
        <div data-testid="admin-feature-requests">
            <div className="flex flex-wrap gap-1 mb-4">
                {["all", "open", "planned", "in_progress", "done", "declined"].map((k) => (
                    <button key={k} data-testid={`fr-filter-${k}`} onClick={() => setFilter(k)}
                        className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] border ${filter === k ? "border-[#ff4500] text-[#ff4500]" : "border-[#2a2a2a] text-[#a0a0a0] hover:text-white"}`}>
                        {k === "all" ? "All" : (FR_STATUS_META[k]?.label || k)} {k === "all" ? `(${rows.length})` : (counts[k] ? `(${counts[k]})` : "")}
                    </button>
                ))}
            </div>
            {loading ? <p className="text-[#a0a0a0] text-sm">Loading…</p>
                : shown.length === 0 ? <p className="text-[#a0a0a0] text-sm">No feature requests yet.</p>
                    : (
                        <div className="space-y-3">
                            {shown.map((r) => {
                                const meta = FR_STATUS_META[r.status || "open"] || FR_STATUS_META.open;
                                return (
                                    <div key={r.id} className="border border-[#2a2a2a] bg-[#141414] p-4" data-testid={`fr-row-${r.id}`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className={`text-[9px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${meta.cls}`}>{meta.label}</span>
                                                    <h4 className="font-semibold text-white">{r.title}</h4>
                                                </div>
                                                {r.description && <p className="text-sm text-[#cfcfcf] mt-1.5 whitespace-pre-wrap">{r.description}</p>}
                                                <p className="text-[11px] text-[#6a6a6a] mt-2">by @{r.username} · {new Date(r.created_at).toLocaleDateString()}</p>
                                            </div>
                                            <button data-testid={`fr-delete-${r.id}`} onClick={() => remove(r.id)} className="text-[#6a6a6a] hover:text-red-500 shrink-0"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-3">
                                            {["open", "planned", "in_progress", "done", "declined"].map((s) => (
                                                <button key={s} data-testid={`fr-set-${r.id}-${s}`} onClick={() => setStatus(r.id, s)} disabled={(r.status || "open") === s}
                                                    className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] border ${(r.status || "open") === s ? "border-[#ff4500] text-[#ff4500]" : "border-[#2a2a2a] text-[#a0a0a0] hover:text-white"} disabled:opacity-100`}>
                                                    {FR_STATUS_META[s].label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
        </div>
    );
}
